import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { format, parseISO, subDays, startOfMonth, endOfMonth } from "date-fns";
import { it } from "date-fns/locale";
import {
  Search,
  Plus,
  ArrowLeft,
  Save,
  Printer,
  Trash2,
  Package,
  Calendar,
  Clock,
  Flag,
  ChevronDown,
  ChevronRight,
  X,
  Phone,
  Mail,
  User,
  PlusCircle,
  MessageCircle,
  Loader2,
  BarChart3,
  Download,
  FileText,
  TrendingUp,
  Euro,
  ShoppingBag,
  Users,
  FileSpreadsheet,
  UserCircle,
  Eye,
  Warehouse,
  BoxesIcon,
  Edit,
  QrCode,
  Award,
  Star,
  Gift,
  Share2,
  Settings,
  Building2,
  Lock,
  Shield,
  LogOut,
  ChevronLeft,
  Palette,
  KeyRound
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Session key for auth data
const SESSION_KEY = "scd_auth";

// Axios interceptor: add X-Company-ID and Authorization headers
axios.interceptors.request.use((config) => {
  try {
    const auth = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
    if (auth.company_id) {
      config.headers["X-Company-ID"] = auth.company_id;
    }
    if (auth.token && auth.role === "super_admin") {
      config.headers["Authorization"] = `Bearer ${auth.token}`;
    }
  } catch {}
  return config;
});

// Helper to get company info from session
const getCompanyInfo = () => {
  try {
    const auth = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
    return {
      name: auth.company_name || "Azienda",
      tagline: auth.company_tagline || "",
      id: auth.company_id || "",
      card_color: auth.company_card_color || "#58A6FF",
      logo: auth.company_logo || ""
    };
  } catch { return { name: "Azienda", tagline: "", id: "", card_color: "#58A6FF", logo: "" }; }
};

// Order status colors
const STATUS_COLORS = {
  "Inserito": "#58A6FF",
  "Avviato": "#F0883E",
  "Bozza": "#8B949E",
  "Preventivo": "#79C0FF",
  "Attesa risposta": "#A371F7",
  "Allestimento": "#39D353",
  "Da Pagare": "#E3B341",
  "Da Fatturare": "#D2A8FF",
  "Consegna Avvisata": "#F778BA",
  "Consegnato": "#238636"
};

const ORDER_STATUSES = ["Inserito", "Avviato", "Bozza", "Preventivo", "Attesa risposta", "Allestimento", "Da Pagare", "Da Fatturare", "Consegna Avvisata", "Consegnato"];

// Format date for display
const formatDateDisplay = (dateString) => {
  if (!dateString) return "Seleziona data";
  try {
    return format(parseISO(dateString), "dd MMMM yyyy", { locale: it });
  } catch {
    return dateString;
  }
};

// Format date for input
const formatDateForInput = (dateString) => {
  if (!dateString) return "";
  try {
    return format(parseISO(dateString), "yyyy-MM-dd");
  } catch {
    return dateString;
  }
};

// Get today's date in ISO format
const getTodayISO = () => format(new Date(), "yyyy-MM-dd");

// ============ EXPORT UTILITIES ============

// Export orders to CSV
const exportToCSV = (orders, filename = "ordini") => {
  const headers = [
    "ID", "Nome", "Cognome", "Telefono", "Email", "Stato",
    "Data Arrivo", "Data Consegna Prevista", "Data Consegna Richiesta",
    "Num. Prodotti", "Totale €", "Data Creazione"
  ];

  const rows = orders.map(order => {
    const total = order.products?.reduce((sum, p) => sum + (p.price * p.quantity), 0) || 0;
    return [
      order.id,
      order.first_name,
      order.last_name,
      order.phone,
      order.email,
      order.status,
      order.arrival_date,
      order.expected_delivery_date,
      order.requested_delivery_date,
      order.products?.length || 0,
      total.toFixed(2),
      order.created_at ? format(new Date(order.created_at), "dd/MM/yyyy HH:mm") : ""
    ];
  });

  const csvContent = [
    headers.join(";"),
    ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(";"))
  ].join("\n");

  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${format(new Date(), "yyyy-MM-dd")}.csv`;
  link.click();
};

// Export single order to PDF (printable HTML - A5 format)
const exportOrderToPDF = (order, withIva = false) => {
  const total = order.products?.reduce((sum, p) => sum + (p.price * p.quantity), 0) || 0;
  const ivaAmount = total * 0.22;
  const totalWithIva = total * 1.22;
  const ci = getCompanyInfo();
  const logoHtml = ci.logo ? `<img src="${BACKEND_URL}${ci.logo}" style="height:40px;margin-bottom:6px" />` : '';
  
  const printWindow = window.open('', '_blank');
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Ordine - ${order.first_name} ${order.last_name}</title>
      <style>
        @page {
          size: A5 portrait;
          margin: 10mm;
        }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: Arial, sans-serif; 
          padding: 10mm; 
          color: #333; 
          width: 148mm;
          max-width: 148mm;
          font-size: 11px;
        }
        .header { text-align: center; margin-bottom: 12px; padding-bottom: 10px; border-bottom: 2px solid #333; }
        .header h1 { font-size: 16px; margin-bottom: 4px; }
        .header p { color: #666; font-size: 10px; }
        .section { margin-bottom: 12px; }
        .section h2 { font-size: 12px; color: #333; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
        .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px; }
        .info-item { margin-bottom: 4px; }
        .info-item label { font-size: 9px; color: #666; display: block; margin-bottom: 2px; }
        .info-item span { font-size: 11px; font-weight: 500; }
        .status { display: inline-block; padding: 3px 10px; border-radius: 10px; font-weight: 600; font-size: 11px; }
        .products-table { width: 100%; border-collapse: collapse; margin-top: 8px; }
        .products-table th, .products-table td { border: 1px solid #ddd; padding: 5px 6px; text-align: left; font-size: 10px; }
        .products-table th { background: #f5f5f5; font-weight: 600; font-size: 9px; }
        .products-table .price { text-align: right; }
        .total-row { background: #f0f9ff !important; font-weight: bold; }
        .total-row td { font-size: 12px; }
        .footer { margin-top: 15px; padding-top: 8px; border-top: 1px solid #ddd; text-align: center; color: #999; font-size: 9px; }
        @media print { 
          body { padding: 0; width: auto; max-width: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        ${logoHtml}
        <h1>ORDINE #${order.id.slice(-8).toUpperCase()}</h1>
        <p>Data: ${format(new Date(), "dd MMMM yyyy", { locale: it })}</p>
      </div>

      <div class="section">
        <h2>Stato Ordine</h2>
        <span class="status" style="background: ${STATUS_COLORS[order.status]}20; color: ${STATUS_COLORS[order.status]}; border: 2px solid ${STATUS_COLORS[order.status]}">
          ${order.status}
        </span>
      </div>

      <div class="section">
        <h2>Informazioni Cliente</h2>
        <div class="info-grid">
          <div class="info-item">
            <label>Nome e Cognome</label>
            <span>${order.first_name} ${order.last_name}</span>
          </div>
          <div class="info-item">
            <label>Telefono</label>
            <span>${order.phone || '-'}</span>
          </div>
          <div class="info-item">
            <label>Email</label>
            <span>${order.email || '-'}</span>
          </div>
        </div>
      </div>

      <div class="section">
        <h2>Date</h2>
        <div class="info-grid">
          <div class="info-item">
            <label>Data Arrivo</label>
            <span>${formatDateDisplay(order.arrival_date)}</span>
          </div>
          <div class="info-item">
            <label>Consegna Prevista</label>
            <span>${formatDateDisplay(order.expected_delivery_date)}</span>
          </div>
          <div class="info-item">
            <label>Consegna Richiesta</label>
            <span>${formatDateDisplay(order.requested_delivery_date)}</span>
          </div>
        </div>
      </div>

      <div class="section">
        <h2>Prodotti</h2>
        <table class="products-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Descrizione</th>
              <th class="price">Qtà</th>
              <th class="price">Prezzo</th>
              <th class="price">Totale</th>
            </tr>
          </thead>
          <tbody>
            ${order.products?.map((p, i) => `
              <tr>
                <td>${i + 1}</td>
                <td>${p.text_notes || 'Prodotto senza descrizione'}</td>
                <td class="price">${p.quantity}</td>
                <td class="price">€ ${p.price.toFixed(2)}</td>
                <td class="price">€ ${(p.price * p.quantity).toFixed(2)}</td>
              </tr>
            `).join('') || '<tr><td colspan="5">Nessun prodotto</td></tr>'}
            <tr class="total-row">
              <td colspan="4">TOTALE IMPONIBILE</td>
              <td class="price">€ ${total.toFixed(2)}</td>
            </tr>
            ${withIva ? `
            <tr>
              <td colspan="4" style="text-align:right;font-size:11px;color:#555">IVA 22%</td>
              <td class="price" style="font-size:11px">€ ${ivaAmount.toFixed(2)}</td>
            </tr>
            <tr class="total-row" style="background:#e8f5e9 !important">
              <td colspan="4" style="font-size:13px"><strong>TOTALE CON IVA</strong></td>
              <td class="price" style="font-size:14px"><strong>€ ${totalWithIva.toFixed(2)}</strong></td>
            </tr>
            ` : ''}
          </tbody>
        </table>
      </div>

      <div class="footer">
        <p>Documento generato il ${format(new Date(), "dd/MM/yyyy 'alle' HH:mm")}</p>
      </div>

      <script>window.onload = function() { window.print(); }</script>
    </body>
    </html>
  `);
  printWindow.document.close();
};

// ============ COMPONENTS ============

// Status Badge Component
const StatusBadge = ({ status, onClick, small = false }) => {
  const color = STATUS_COLORS[status] || "#8B949E";
  return (
    <button
      data-testid="status-badge"
      onClick={onClick}
      className={`status-badge ${small ? 'py-1 px-3 text-sm' : ''}`}
      style={{ borderColor: color }}
    >
      <span className="status-dot" style={{ backgroundColor: color }} />
      <span style={{ color }} className="font-semibold">{status}</span>
      {onClick && <ChevronDown size={small ? 14 : 18} style={{ color }} />}
    </button>
  );
};

// Status Modal Component
const StatusModal = ({ isOpen, onClose, currentStatus, onSelect }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 50, WebkitOverflowScrolling: 'touch', cursor: 'pointer' }}
      onClick={onClose}
      onTouchEnd={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div 
        style={{ backgroundColor: '#161B22', width: '100%', maxWidth: '32rem', borderTopLeftRadius: '1rem', borderTopRightRadius: '1rem', cursor: 'default' }}
        onClick={e => e.stopPropagation()}
        onTouchEnd={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem', borderBottom: '1px solid #30363D' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#FFFFFF', margin: 0 }}>Seleziona Stato</h3>
          <button onClick={onClose} onTouchEnd={(e) => { e.preventDefault(); onClose(); }} style={{ color: '#8B949E', background: 'none', border: 'none', padding: '8px', cursor: 'pointer', WebkitAppearance: 'none' }}>
            <X size={24} />
          </button>
        </div>
        <div style={{ padding: '1rem', maxHeight: '60vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {ORDER_STATUSES.map(status => (
            <button
              key={status}
              data-testid={`status-option-${status.toLowerCase().replace(/\s/g, '-')}`}
              onClick={() => { onSelect(status); onClose(); }}
              onTouchEnd={(e) => { e.preventDefault(); onSelect(status); onClose(); }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: '1rem',
                borderRadius: '0.75rem',
                marginBottom: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                WebkitAppearance: 'none',
                backgroundColor: currentStatus === status ? '#0D419D' : '#21262D',
                transition: 'background-color 0.2s'
              }}
            >
              <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: STATUS_COLORS[status], marginRight: '12px', flexShrink: 0 }} />
              <span style={{ flex: 1, textAlign: 'left', color: currentStatus === status ? '#FFFFFF' : '#C9D1D9', fontWeight: currentStatus === status ? '600' : '400', fontSize: '1rem' }}>
                {status}
              </span>
              {currentStatus === status && <span style={{ color: '#FFFFFF', marginLeft: '8px' }}>✓</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Product Modal Component
const ProductModal = ({ isOpen, onClose, product, onSave, onDelete }) => {
  const [textNotes, setTextNotes] = useState(product?.text_notes || "");
  const [quantity, setQuantity] = useState(String(product?.quantity || 1));
  const [price, setPrice] = useState(String(product?.price || 0));
  const [saving, setSaving] = useState(false);
  const [inventoryItemId, setInventoryItemId] = useState(product?.inventory_item_id || null);
  const [inventorySearch, setInventorySearch] = useState("");
  const [inventorySuggestions, setInventorySuggestions] = useState([]);
  const [showInventory, setShowInventory] = useState(false);
  const [selectedItemName, setSelectedItemName] = useState("");
  const [selectedItemStock, setSelectedItemStock] = useState(null);
  const inventoryRef = useRef(null);

  useEffect(() => {
    if (product) {
      setTextNotes(product.text_notes || "");
      setQuantity(String(product.quantity || 1));
      setPrice(String(product.price || 0));
      setInventoryItemId(product.inventory_item_id || null);
      setSelectedItemName(product.inventory_item_name || "");
    } else {
      setTextNotes("");
      setQuantity("1");
      setPrice("");
      setInventoryItemId(null);
      setSelectedItemName("");
      setSelectedItemStock(null);
    }
    setInventorySearch("");
    setShowInventory(false);
  }, [product, isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (inventoryRef.current && !inventoryRef.current.contains(e.target)) {
        setShowInventory(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const searchInventory = async (query) => {
    if (query.length < 1) {
      // Show all items if empty
      try {
        const response = await axios.get(`${API}/inventory`);
        setInventorySuggestions(response.data);
        setShowInventory(response.data.length > 0);
      } catch { setInventorySuggestions([]); }
      return;
    }
    try {
      const response = await axios.get(`${API}/inventory/search?q=${encodeURIComponent(query)}`);
      setInventorySuggestions(response.data);
      setShowInventory(response.data.length > 0);
    } catch { setInventorySuggestions([]); }
  };

  const handleSelectInventoryItem = (item) => {
    setInventoryItemId(item.id);
    setSelectedItemName(item.name);
    setSelectedItemStock(item.quantity);
    setTextNotes(item.name);
    setPrice(String(item.cost_price));
    setShowInventory(false);
    setInventorySearch("");
  };

  const handleClearInventory = () => {
    setInventoryItemId(null);
    setSelectedItemName("");
    setSelectedItemStock(null);
  };

  const numericPrice = parseFloat(price) || 0;
  const numericQuantity = parseInt(quantity) || 1;

  const handleSave = async () => {
    setSaving(true);
    await onSave({ 
      text_notes: textNotes, 
      quantity: numericQuantity, 
      price: numericPrice,
      inventory_item_id: inventoryItemId
    });
    setSaving(false);
  };

  if (!isOpen) return null;

  const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem', WebkitOverflowScrolling: 'touch', cursor: 'pointer' };
  const modalStyle = { backgroundColor: '#161B22', width: '100%', maxWidth: '32rem', borderRadius: '1rem', maxHeight: '90vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', cursor: 'default' };
  const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem', borderBottom: '1px solid #30363D' };
  const inputStyle = { width: '100%', backgroundColor: '#0D1117', border: '1px solid #30363D', borderRadius: '8px', padding: '0.875rem', fontSize: '16px', color: '#FFFFFF', WebkitAppearance: 'none' };
  const labelStyle = { fontSize: '0.875rem', color: '#8B949E', marginBottom: '0.5rem', display: 'block' };
  const btnBase = { border: 'none', cursor: 'pointer', borderRadius: '0.75rem', padding: '0.75rem 1rem', fontSize: '1rem', WebkitAppearance: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' };

  return (
    <div style={overlayStyle} onClick={onClose} onTouchEnd={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle} onClick={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()}>
        <div style={headerStyle}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#FFFFFF', margin: 0 }}>
            {product?.id ? "Modifica Prodotto" : "Nuovo Prodotto"}
          </h3>
          <button onClick={onClose} onTouchEnd={(e) => { e.preventDefault(); onClose(); }} style={{ color: '#8B949E', background: 'none', border: 'none', padding: '8px', cursor: 'pointer', WebkitAppearance: 'none' }}>
            <X size={24} />
          </button>
        </div>
        
        <div style={{ padding: '1.25rem' }}>
          {/* Inventory Link */}
          <div ref={inventoryRef} style={{ position: 'relative', marginBottom: '1rem' }}>
            <label style={labelStyle}>Articolo da Magazzino</label>
            {inventoryItemId ? (
              <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#0D1117', border: '1px solid #F0883E', borderRadius: '8px', padding: '0.75rem' }}>
                <Warehouse size={18} style={{ color: '#F0883E', flexShrink: 0, marginRight: '8px' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ color: '#FFFFFF', fontWeight: 500, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedItemName}</p>
                  {selectedItemStock !== null && <p style={{ color: '#8B949E', fontSize: '0.75rem', margin: '2px 0 0' }}>Disponibili: {selectedItemStock}</p>}
                </div>
                <button onClick={handleClearInventory} onTouchEnd={(e) => { e.preventDefault(); handleClearInventory(); }} style={{ color: '#8B949E', background: 'none', border: 'none', padding: '4px', cursor: 'pointer', WebkitAppearance: 'none' }}>
                  <X size={16} />
                </button>
              </div>
            ) : (
              <>
                <input
                  data-testid="inventory-search-in-product"
                  type="text"
                  value={inventorySearch}
                  onChange={e => { setInventorySearch(e.target.value); searchInventory(e.target.value); }}
                  onFocus={() => searchInventory(inventorySearch)}
                  placeholder="Cerca nel magazzino..."
                  style={inputStyle}
                  autoComplete="off"
                />
                {showInventory && inventorySuggestions.length > 0 && (
                  <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', marginTop: '4px', zIndex: 30, backgroundColor: '#21262D', border: '1px solid #F0883E', borderRadius: '0.75rem', maxHeight: '12rem', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#8B949E', borderBottom: '1px solid #30363D' }}>Seleziona articolo</div>
                    {inventorySuggestions.map(item => (
                      <button
                        key={item.id}
                        data-testid={`inv-suggestion-${item.id}`}
                        onClick={() => handleSelectInventoryItem(item)}
                        onTouchEnd={(e) => { e.preventDefault(); handleSelectInventoryItem(item); }}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', background: 'none', border: 'none', borderBottom: '1px solid #30363D', textAlign: 'left', cursor: 'pointer', WebkitAppearance: 'none' }}
                      >
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <p style={{ color: '#FFFFFF', fontWeight: 500, margin: 0 }}>{item.name}</p>
                          <p style={{ color: '#8B949E', fontSize: '0.75rem', margin: '2px 0 0' }}>€ {item.cost_price.toFixed(2)} • Disp: {item.quantity}</p>
                        </div>
                        <span style={{ fontSize: '0.875rem', fontWeight: 600, marginLeft: '8px', color: item.quantity <= 0 ? '#FF6B6B' : item.quantity <= 5 ? '#F0883E' : '#238636' }}>
                          {item.quantity}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Note Prodotto</label>
            <textarea
              data-testid="product-notes-input"
              value={textNotes}
              onChange={e => setTextNotes(e.target.value)}
              placeholder="Descrizione del prodotto..."
              style={{ ...inputStyle, minHeight: '80px', resize: 'none' }}
            />
          </div>
          
          {/* Quantity and Price */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={labelStyle}>Quantità</label>
              <input
                data-testid="product-quantity-input"
                type="number"
                min="1"
                inputMode="numeric"
                value={quantity}
                onChange={e => setQuantity(e.target.value)}
                onFocus={e => e.target.select()}
                style={inputStyle}
              />
              {inventoryItemId && selectedItemStock !== null && numericQuantity > selectedItemStock && (
                <p style={{ color: '#FF6B6B', fontSize: '0.75rem', marginTop: '4px' }}>Supera la disponibilità ({selectedItemStock})</p>
              )}
            </div>
            <div>
              <label style={labelStyle}>Prezzo (€)</label>
              <input
                data-testid="product-price-input"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={price}
                onChange={e => setPrice(e.target.value)}
                onFocus={e => e.target.select()}
                style={inputStyle}
              />
            </div>
          </div>
          
          {/* Total */}
          <div style={{ backgroundColor: '#21262D', borderRadius: '8px', padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span style={{ color: '#8B949E' }}>Totale</span>
            <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#238636' }}>
              € {(numericPrice * numericQuantity).toFixed(2)}
            </span>
          </div>
        </div>
        
        {/* Footer buttons */}
        <div style={{ padding: '1.25rem', borderTop: '1px solid #30363D', display: 'flex', gap: '0.75rem' }}>
          {product?.id && (
            <button
              data-testid="product-delete-btn"
              onClick={() => { onDelete(product.id); onClose(); }}
              onTouchEnd={(e) => { e.preventDefault(); onDelete(product.id); onClose(); }}
              style={{ ...btnBase, backgroundColor: 'rgba(239,68,68,0.2)', color: '#FF6B6B' }}
            >
              <Trash2 size={20} />
            </button>
          )}
          <button
            onClick={onClose}
            onTouchEnd={(e) => { e.preventDefault(); onClose(); }}
            style={{ ...btnBase, flex: 1, backgroundColor: '#21262D', color: '#C9D1D9' }}
          >
            Annulla
          </button>
          <button
            data-testid="product-save-btn"
            onClick={handleSave}
            onTouchEnd={(e) => { e.preventDefault(); handleSave(); }}
            disabled={saving}
            style={{ ...btnBase, flex: 1, backgroundColor: '#238636', color: '#FFFFFF', gap: '8px' }}
          >
            {saving ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
            Salva
          </button>
        </div>
      </div>
    </div>
  );
};

// Order Card Component with product peek preview
const OrderCard = ({ order, onClick, onStatusChange }) => {
  const statusColor = STATUS_COLORS[order.status] || "#8B949E";
  const total = order.products?.reduce((sum, p) => sum + (p.price * p.quantity), 0) || 0;
  const displayTotal = order.iva ? total * 1.22 : total;
  const [peeking, setPeeking] = useState(false);
  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const longPressTimer = useRef(null);
  const didLongPress = useRef(false);

  const startPeek = () => {
    didLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      setPeeking(true);
    }, 400);
  };

  const endPeek = () => {
    clearTimeout(longPressTimer.current);
    if (peeking) {
      setPeeking(false);
    }
    setTimeout(() => { didLongPress.current = false; }, 50);
  };

  const handleClick = (e) => {
    if (didLongPress.current) {
      e.preventDefault();
      return;
    }
    onClick();
  };

  return (
    <div
      data-testid={`order-card-${order.id}`}
      onClick={handleClick}
      onMouseEnter={() => setPeeking(true)}
      onMouseLeave={() => setPeeking(false)}
      onTouchStart={startPeek}
      onTouchEnd={endPeek}
      onTouchCancel={endPeek}
      className="order-card bg-[#161B22] border border-[#30363D] rounded-xl p-4 cursor-pointer relative"
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-white font-semibold">{order.first_name} {order.last_name}</h3>
          {order.phone && <p className="text-[#8B949E] text-sm">{order.phone}</p>}
        </div>
        <div onClick={e => { e.stopPropagation(); setShowStatusPicker(true); setPeeking(false); }} className="cursor-pointer">
          <StatusBadge status={order.status} small />
        </div>
      </div>
      
      <div className="text-sm text-[#8B949E] mb-3">
        <div className="flex items-center gap-2 mb-1">
          <Calendar size={14} />
          <span>Arrivo: {formatDateDisplay(order.arrival_date)}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock size={14} />
          <span>Consegna: {formatDateDisplay(order.expected_delivery_date)}</span>
        </div>
      </div>
      
      <div className="flex justify-between items-center pt-3 border-t border-[#30363D]">
        <span className="text-[#8B949E] text-sm">{order.products?.length || 0} prodotti</span>
        <span className="text-[#238636] font-bold">
          € {displayTotal.toFixed(2)}
          {order.iva && <span className="text-[#8B949E] text-xs font-normal ml-1">(IVA)</span>}
        </span>
      </div>

      {/* Product peek overlay */}
      {peeking && !showStatusPicker && order.products?.length > 0 && (
        <div
          className="absolute inset-0 bg-[#161B22]/98 backdrop-blur-sm rounded-xl p-4 z-10 border border-[#58A6FF]/40 overflow-y-auto"
          style={{ animation: 'fadeIn 0.15s ease-out' }}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-[#58A6FF] text-xs font-semibold uppercase tracking-wider">Prodotti ({order.products.length})</p>
            <p className="text-[#8B949E] text-xs">#{order.id.slice(-6).toUpperCase()}</p>
          </div>
          <div className="space-y-2">
            {order.products.map((p, i) => (
              <div key={p.id || i} className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[#C9D1D9] text-sm truncate">{p.text_notes || `Prodotto ${i + 1}`}</p>
                  <p className="text-[#6E7681] text-xs">Qtà: {p.quantity}</p>
                </div>
                <p className="text-[#58A6FF] text-sm font-medium shrink-0">€ {(p.price * p.quantity).toFixed(2)}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-2 border-t border-[#30363D] flex justify-between">
            <span className="text-[#8B949E] text-xs">Totale{order.iva ? ' (IVA incl.)' : ''}</span>
            <span className="text-[#238636] font-bold text-sm">€ {displayTotal.toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* Inline status picker */}
      {showStatusPicker && (
        <StatusModal
          isOpen={true}
          currentStatus={order.status}
          onSelect={async (newStatus) => {
            try {
              await axios.put(`${API}/orders/${order.id}`, { status: newStatus });
              if (onStatusChange) onStatusChange(order.id, newStatus);
              toast.success(`Stato → ${newStatus}`);
            } catch { toast.error("Errore cambio stato"); }
          }}
          onClose={() => setShowStatusPicker(false)}
        />
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subValue, color = "#58A6FF" }) => (
  <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-5 hover:border-[#58A6FF] transition-colors">
    <div className="flex items-start justify-between mb-3">
      <div 
        className="w-12 h-12 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: `${color}20` }}
      >
        <Icon size={24} style={{ color }} />
      </div>
      {subValue && (
        <span className="text-xs px-2 py-1 rounded-full bg-[#238636]/20 text-[#238636]">
          {subValue}
        </span>
      )}
    </div>
    <p className="text-[#8B949E] text-sm mb-1">{label}</p>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

// Customer Autocomplete Component
const CustomerAutocomplete = ({ onSelect, firstName, lastName, setFirstName, setLastName, setPhone, setEmail }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const searchCustomers = async (query) => {
    if (query.length < 1) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/customers/search?q=${encodeURIComponent(query)}`);
      setSuggestions(response.data);
      setShowSuggestions(response.data.length > 0);
    } catch { 
      setSuggestions([]);
    }
  };

  const handleNameChange = (field, value) => {
    if (field === 'first') setFirstName(value);
    else setLastName(value);

    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      const query = field === 'first' ? value : `${firstName} ${value}`.trim();
      if (query.length >= 1) searchCustomers(query);
    }, 300);
    setSearchTimeout(timeout);
  };

  const handleSelectCustomer = (customer) => {
    setFirstName(customer.first_name);
    setLastName(customer.last_name);
    if (customer.phone) setPhone(customer.phone);
    if (customer.email) setEmail(customer.email);
    setShowSuggestions(false);
    setSuggestions([]);
    toast.success(`Cliente ${customer.first_name} ${customer.last_name} selezionato`);
  };

  return (
    <div ref={wrapperRef} className="relative space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="field-label">Nome *</label>
          <input
            data-testid="first-name-input"
            type="text"
            value={firstName}
            onChange={e => handleNameChange('first', e.target.value)}
            onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
            placeholder="Inserisci il nome"
            className="field-input"
            autoComplete="off"
          />
        </div>
        <div>
          <label className="field-label">Cognome *</label>
          <input
            data-testid="last-name-input"
            type="text"
            value={lastName}
            onChange={e => handleNameChange('last', e.target.value)}
            onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
            placeholder="Inserisci il cognome"
            className="field-input"
            autoComplete="off"
          />
        </div>
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-[76px] z-30 bg-[#21262D] border border-[#58A6FF] rounded-xl shadow-lg max-h-48 overflow-y-auto animate-fadeIn">
          <div className="px-3 py-2 text-xs text-[#8B949E] border-b border-[#30363D]">
            Clienti trovati — tocca per compilare
          </div>
          {suggestions.map(customer => (
            <button
              key={customer.id}
              data-testid={`customer-suggestion-${customer.id}`}
              onClick={() => handleSelectCustomer(customer)}
              className="w-full flex items-center gap-3 px-3 py-3 hover:bg-[#30363D] transition-colors text-left border-b border-[#30363D] last:border-0"
            >
              <UserCircle size={20} className="text-[#58A6FF] flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-white font-medium truncate">{customer.first_name} {customer.last_name}</p>
                <p className="text-[#8B949E] text-xs truncate">
                  {customer.phone && customer.phone !== '+39' ? customer.phone : ''} 
                  {customer.email ? ` • ${customer.email}` : ''}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Dashboard Page
const Dashboard = ({ onNavigate, isAdmin = false, perms = {}, accountName = "", onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => format(new Date(), "yyyy-MM"));

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  // Build list of months that have orders
  const availableMonths = (() => {
    const months = new Set();
    orders.forEach(order => {
      const d = new Date(order.created_at);
      if (!isNaN(d)) months.add(format(d, "yyyy-MM"));
    });
    // Always include current month
    months.add(format(new Date(), "yyyy-MM"));
    return [...months].sort().reverse();
  })();

  // Filter orders for selected month
  const monthStart = startOfMonth(parseISO(`${selectedMonth}-01`));
  const monthEnd = endOfMonth(parseISO(`${selectedMonth}-01`));
  const monthOrders = orders.filter(order => {
    const d = new Date(order.created_at);
    return d >= monthStart && d <= monthEnd;
  });

  // All-time stats
  const allTimeRevenue = orders.reduce((sum, o) =>
    sum + (o.products?.reduce((s, p) => s + (p.price * p.quantity), 0) || 0), 0);

  // Monthly stats
  const monthRevenue = monthOrders.reduce((sum, o) =>
    sum + (o.products?.reduce((s, p) => s + (p.price * p.quantity), 0) || 0), 0);
  const monthCompleted = monthOrders.filter(o => o.status === "Consegnato").length;
  const monthActive = monthOrders.filter(o => o.status !== "Consegnato").length;
  const monthProducts = monthOrders.reduce((sum, o) => sum + (o.products?.length || 0), 0);

  // Status breakdown for selected month
  const statusCounts = {};
  ORDER_STATUSES.forEach(s => { statusCounts[s] = 0; });
  monthOrders.forEach(o => { if (statusCounts[o.status] !== undefined) statusCounts[o.status]++; });
  const statusBreakdown = Object.entries(statusCounts)
    .filter(([, v]) => v > 0)
    .map(([name, value]) => ({ name, value, color: STATUS_COLORS[name] }));

  // Recent orders for selected month
  const recentMonthOrders = [...monthOrders]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 8);

  const handleExportCSV = () => {
    if (monthOrders.length === 0) {
      toast.error("Nessun ordine da esportare per questo mese");
      return;
    }
    const monthLabel = format(parseISO(`${selectedMonth}-01`), "MMMM_yyyy", { locale: it });
    exportToCSV(monthOrders, `ordini_${monthLabel}`);
    toast.success("Export CSV completato");
  };

  const navigateMonth = (direction) => {
    const current = parseISO(`${selectedMonth}-01`);
    const target = direction === 'prev'
      ? new Date(current.getFullYear(), current.getMonth() - 1, 1)
      : new Date(current.getFullYear(), current.getMonth() + 1, 1);
    // Don't go beyond current month
    if (target > new Date()) return;
    setSelectedMonth(format(target, "yyyy-MM"));
  };

  if (loading) {
    return (
      <div className="app-container min-h-screen flex flex-col items-center justify-center">
        <Loader2 size={48} className="text-[#58A6FF] animate-spin" />
        <p className="text-[#8B949E] mt-4">Caricamento dashboard...</p>
      </div>
    );
  }

  const selectedMonthLabel = format(parseISO(`${selectedMonth}-01`), "MMMM yyyy", { locale: it });
  const isCurrentMonth = selectedMonth === format(new Date(), "yyyy-MM");

  return (
    <div className="app-container min-h-screen">
      {/* Header */}
      <header className="app-header">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <BarChart3 size={24} className="text-[#58A6FF]" />
              <h1 className="text-xl sm:text-2xl font-bold text-white">Dashboard</h1>
              {accountName && <span className="text-[#8B949E] text-sm hidden sm:inline">• {accountName}</span>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {perms.export && (
                <button
                  data-testid="export-csv-btn"
                  onClick={handleExportCSV}
                  className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-3 py-2 rounded-lg hover:bg-[#30363D] transition-colors text-sm"
                >
                  <FileSpreadsheet size={16} />
                  <span className="hidden sm:inline">Export CSV</span>
                </button>
              )}
              {perms.orders && (
                <button
                  data-testid="go-to-orders-btn"
                  onClick={() => onNavigate('list')}
                  className="flex items-center gap-1.5 bg-[#238636] text-white px-3 py-2 rounded-lg hover:bg-[#2ea043] transition-colors text-sm"
                >
                  <Package size={16} />
                  <span className="hidden sm:inline">Ordini</span>
                </button>
              )}
              {perms.customers && (
                <button
                  data-testid="go-to-customers-btn"
                  onClick={() => onNavigate('customers')}
                  className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-3 py-2 rounded-lg hover:bg-[#30363D] transition-colors text-sm"
                >
                  <Users size={16} />
                  <span className="hidden sm:inline">Clienti</span>
                </button>
              )}
              {perms.inventory && (
                <button
                  data-testid="go-to-inventory-btn"
                  onClick={() => onNavigate('inventory')}
                  className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-3 py-2 rounded-lg hover:bg-[#30363D] transition-colors text-sm"
                >
                  <Warehouse size={16} />
                  <span className="hidden sm:inline">Magazzino</span>
                </button>
              )}
              {perms.scanner && (
                <button
                  data-testid="go-to-scanner-btn"
                  onClick={() => onNavigate('scanner')}
                  className="flex items-center gap-1.5 bg-[#F0883E]/20 text-[#F0883E] px-3 py-2 rounded-lg hover:bg-[#F0883E]/30 transition-colors text-sm border border-[#F0883E]/40"
                >
                  <QrCode size={16} />
                  <span className="hidden sm:inline">Scanner</span>
                </button>
              )}
              {isAdmin && (
                <button
                  data-testid="go-to-settings-btn"
                  onClick={() => onNavigate('settings')}
                  className="flex items-center gap-1.5 bg-[#21262D] text-[#8B949E] px-3 py-2 rounded-lg hover:bg-[#30363D] transition-colors text-sm"
                >
                  <Settings size={16} />
                </button>
              )}
              <button
                data-testid="logout-btn"
                onClick={onLogout}
                className="flex items-center gap-1.5 bg-red-500/10 text-red-400 px-3 py-2 rounded-lg hover:bg-red-500/20 transition-colors text-sm"
                title="Esci"
              >
                <ArrowLeft size={16} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto p-4 space-y-4">
        {/* Month Selector */}
        <div className="flex items-center justify-center gap-3">
          <button
            data-testid="prev-month-btn"
            onClick={() => navigateMonth('prev')}
            className="p-2 bg-[#21262D] text-[#C9D1D9] rounded-lg hover:bg-[#30363D] transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl px-5 py-3 text-center min-w-[200px]">
            <p className="text-white font-bold text-lg capitalize">{selectedMonthLabel}</p>
            {isCurrentMonth && <p className="text-[#58A6FF] text-xs">Mese corrente</p>}
          </div>
          <button
            data-testid="next-month-btn"
            onClick={() => navigateMonth('next')}
            disabled={isCurrentMonth}
            className={`p-2 rounded-lg transition-colors ${isCurrentMonth ? 'bg-[#161B22] text-[#30363D] cursor-not-allowed' : 'bg-[#21262D] text-[#C9D1D9] hover:bg-[#30363D]'}`}
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* All-time summary bar */}
        <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-3 flex flex-wrap items-center justify-center gap-4 text-sm">
          <span className="text-[#8B949E]">Totale generale:</span>
          <span className="text-white font-semibold">{orders.length} ordini</span>
          <span className="text-[#238636] font-semibold">€ {allTimeRevenue.toFixed(2)}</span>
        </div>

        {/* Monthly Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-4 text-center">
            <p className="text-[#8B949E] text-xs">Ordini</p>
            <p className="text-2xl font-bold text-white">{monthOrders.length}</p>
          </div>
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-4 text-center">
            <p className="text-[#8B949E] text-xs">Fatturato</p>
            <p className="text-2xl font-bold text-[#238636]">€ {monthRevenue.toFixed(2)}</p>
          </div>
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-4 text-center">
            <p className="text-[#8B949E] text-xs">Attivi</p>
            <p className="text-2xl font-bold text-[#F0883E]">{monthActive}</p>
          </div>
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-4 text-center">
            <p className="text-[#8B949E] text-xs">Consegnati</p>
            <p className="text-2xl font-bold text-[#58A6FF]">{monthCompleted}</p>
          </div>
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-4 text-center col-span-2 sm:col-span-1">
            <p className="text-[#8B949E] text-xs">Prodotti</p>
            <p className="text-2xl font-bold text-[#A371F7]">{monthProducts}</p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Pie Chart */}
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-5">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <BarChart3 size={20} className="text-[#58A6FF]" />
              Ordini per Stato
            </h3>
            {statusBreakdown.length > 0 ? (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={false}
                    >
                      {statusBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#161B22', 
                        border: '1px solid #30363D',
                        borderRadius: '8px',
                        color: '#C9D1D9'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-[#8B949E]">
                Nessun ordine in {selectedMonthLabel}
              </div>
            )}
          </div>

          {/* Status Legend */}
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-5">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Flag size={20} className="text-[#A371F7]" />
              Riepilogo Stati
            </h3>
            <div className="space-y-3">
              {ORDER_STATUSES.map(status => {
                const count = statusCounts[status] || 0;
                const percentage = monthOrders.length > 0 
                  ? ((count / monthOrders.length) * 100).toFixed(1) 
                  : 0;
                
                return (
                  <div key={status} className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: STATUS_COLORS[status] }}
                    />
                    <span className="flex-1 text-[#C9D1D9]">{status}</span>
                    <span className="text-white font-semibold">{count}</span>
                    <span className="text-[#8B949E] text-sm w-14 text-right">{percentage}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Orders for selected month */}
        <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Clock size={20} className="text-[#F0883E]" />
              Ordini di {selectedMonthLabel}
            </h3>
            <button
              onClick={() => onNavigate('list')}
              className="text-[#58A6FF] text-sm hover:underline"
            >
              Vedi tutti →
            </button>
          </div>
          
          {recentMonthOrders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[#8B949E] text-sm border-b border-[#30363D]">
                    <th className="pb-3 font-medium">Cliente</th>
                    <th className="pb-3 font-medium">Stato</th>
                    <th className="pb-3 font-medium">Prodotti</th>
                    <th className="pb-3 font-medium text-right">Totale</th>
                    <th className="pb-3 font-medium text-right">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {recentMonthOrders.map(order => {
                    const total = order.products?.reduce((sum, p) => sum + (p.price * p.quantity), 0) || 0;
                    const displayTotal = order.iva ? total * 1.22 : total;
                    return (
                      <tr 
                        key={order.id} 
                        className="border-b border-[#30363D] last:border-0 hover:bg-[#21262D] cursor-pointer transition-colors"
                        onClick={() => onNavigate('detail', order.id)}
                      >
                        <td className="py-3">
                          <span className="text-white font-medium">{order.first_name} {order.last_name}</span>
                          <span className="block text-[#8B949E] text-sm">{order.phone}</span>
                        </td>
                        <td className="py-3">
                          <StatusBadge status={order.status} small />
                        </td>
                        <td className="py-3 text-[#C9D1D9]">{order.products?.length || 0}</td>
                        <td className="py-3 text-right text-[#238636] font-semibold">
                          € {displayTotal.toFixed(2)}
                          {order.iva && <span className="text-[#8B949E] text-xs font-normal ml-1">(IVA)</span>}
                        </td>
                        <td className="py-3 text-right text-[#8B949E] text-sm">
                          {order.created_at ? format(new Date(order.created_at), "dd/MM/yyyy") : "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-[#8B949E]">
              <Package size={48} className="mx-auto mb-3 text-[#30363D]" />
              <p>Nessun ordine in {selectedMonthLabel}</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// Orders List Page
const OrdersList = ({ onSelectOrder, onNewOrder, onNavigate, onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("active");

  const fetchOrders = useCallback(async () => {
    try {
      const url = searchQuery.length >= 2 
        ? `${API}/orders/search?q=${encodeURIComponent(searchQuery)}`
        : `${API}/orders`;
      const response = await axios.get(url);
      setOrders(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento ordini");
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Count orders per status
  const statusCounts = {};
  ORDER_STATUSES.forEach(s => { statusCounts[s] = 0; });
  orders.forEach(o => { if (statusCounts[o.status] !== undefined) statusCounts[o.status]++; });
  const activeCount = orders.filter(o => o.status !== "Consegnato").length;

  const filteredOrders = statusFilter === "all"
    ? orders
    : statusFilter === "active"
      ? orders.filter(o => o.status !== "Consegnato")
      : orders.filter(o => o.status === statusFilter);

  const handleExportFiltered = () => {
    if (filteredOrders.length === 0) {
      toast.error("Nessun ordine da esportare");
      return;
    }
    const filename = statusFilter === "all" ? "tutti_ordini" : `ordini_${statusFilter.toLowerCase().replace(/\s/g, '_')}`;
    exportToCSV(filteredOrders, filename);
    toast.success("Export CSV completato");
  };

  const handlePrintOrdersList = () => {
    if (filteredOrders.length === 0) {
      toast.error("Nessun ordine da stampare");
      return;
    }
    const sorted = [...filteredOrders].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    
    const ci = getCompanyInfo();
    const logoHtml = ci.logo ? `<img src="${BACKEND_URL}${ci.logo}" style="height:40px;margin-bottom:6px" />` : '';
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Elenco Ordini - ${ci.name}</title>
        <style>
          @page { size: A4 portrait; margin: 15mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 15mm; color: #333; font-size: 12px; }
          .header { text-align: center; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 2px solid #333; }
          .header h1 { font-size: 20px; margin-bottom: 4px; }
          .header p { color: #666; font-size: 11px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th { background: #f0f0f0; font-weight: 600; text-align: left; padding: 8px 6px; border: 1px solid #ccc; font-size: 11px; }
          td { padding: 7px 6px; border: 1px solid #ddd; font-size: 11px; vertical-align: middle; }
          tr:nth-child(even) { background: #fafafa; }
          .status { display: inline-block; padding: 2px 8px; border-radius: 8px; font-size: 10px; font-weight: 600; }
          .total-row { background: #f0f9ff !important; font-weight: bold; }
          .total-row td { font-size: 13px; padding: 10px 6px; }
          .footer { margin-top: 12px; text-align: center; color: #999; font-size: 10px; border-top: 1px solid #ddd; padding-top: 8px; }
          .text-right { text-align: right; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          ${logoHtml}
          <h1>ELENCO ORDINI</h1>
          <p>${ci.name} - ${ci.tagline || 'Gestione Ordini'}</p>
          <p style="margin-top:3px">${sorted.length} ordini &bull; Generato il ${format(new Date(), "dd/MM/yyyy 'alle' HH:mm")}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width:5%">#</th>
              <th style="width:25%">Cliente</th>
              <th style="width:20%">Telefono</th>
              <th style="width:18%">Stato</th>
              <th style="width:15%" class="text-right">Importo</th>
              <th style="width:17%">Data</th>
            </tr>
          </thead>
          <tbody>
            ${sorted.map((order, i) => {
              const total = order.products?.reduce((sum, p) => sum + (p.price * p.quantity), 0) || 0;
              const statusColor = STATUS_COLORS[order.status] || '#8B949E';
              return '<tr>' +
                '<td>' + (i + 1) + '</td>' +
                '<td><strong>' + order.first_name + ' ' + order.last_name + '</strong></td>' +
                '<td>' + (order.phone || '-') + '</td>' +
                '<td><span class="status" style="background:' + statusColor + '20;color:' + statusColor + ';border:1px solid ' + statusColor + '">' + order.status + '</span></td>' +
                '<td class="text-right"><strong>&euro; ' + total.toFixed(2) + '</strong></td>' +
                '<td>' + (order.created_at ? format(new Date(order.created_at), "dd/MM/yyyy") : '-') + '</td>' +
              '</tr>';
            }).join('')}
            <tr class="total-row">
              <td colspan="4" style="text-align:right">TOTALE</td>
              <td class="text-right">&euro; ${sorted.reduce((sum, o) => sum + (o.products?.reduce((s, p) => s + (p.price * p.quantity), 0) || 0), 0).toFixed(2)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
        <div class="footer">
          <p>${ci.name} &bull; Elenco Ordini</p>
        </div>
        <script>window.onload = function() { window.print(); }<\/script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="app-container min-h-screen">
      {/* Header */}
      <header className="app-header">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-white">Ordini</h1>
              <button
                data-testid="go-to-dashboard-btn"
                onClick={() => onNavigate('dashboard')}
                className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-2.5 py-1.5 rounded-lg hover:bg-[#30363D] transition-colors text-xs sm:text-sm"
              >
                <BarChart3 size={14} />
                <span className="hidden xs:inline">Dashboard</span>
              </button>
              <button
                data-testid="go-to-customers-from-orders-btn"
                onClick={() => onNavigate('customers')}
                className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-2.5 py-1.5 rounded-lg hover:bg-[#30363D] transition-colors text-xs sm:text-sm"
              >
                <Users size={14} />
                <span className="hidden xs:inline">Clienti</span>
              </button>
              <button
                data-testid="go-to-inventory-from-orders-btn"
                onClick={() => onNavigate('inventory')}
                className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-2.5 py-1.5 rounded-lg hover:bg-[#30363D] transition-colors text-xs sm:text-sm"
              >
                <Warehouse size={14} />
                <span className="hidden xs:inline">Magazzino</span>
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="print-orders-btn"
                onClick={handlePrintOrdersList}
                className="flex items-center gap-1.5 bg-[#21262D] text-[#A371F7] px-3 py-2 rounded-lg hover:bg-[#30363D] transition-colors"
                title="Stampa Elenco"
              >
                <Printer size={16} />
                <span className="hidden sm:inline">Stampa</span>
              </button>
              <button
                data-testid="export-orders-btn"
                onClick={handleExportFiltered}
                className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-3 py-2 rounded-lg hover:bg-[#30363D] transition-colors"
                title="Esporta CSV"
              >
                <Download size={16} />
                <span className="hidden sm:inline">CSV</span>
              </button>
              <button
                data-testid="new-order-btn"
                onClick={onNewOrder}
                className="flex items-center gap-1.5 bg-[#238636] text-white px-3 py-2 rounded-lg hover:bg-[#2ea043] transition-colors"
              >
                <Plus size={18} />
                <span className="hidden sm:inline">Nuovo Ordine</span>
              </button>
              {onLogout && (
                <button onClick={onLogout} className="flex items-center gap-1.5 bg-red-500/10 text-red-400 px-2.5 py-2 rounded-lg hover:bg-red-500/20 transition-colors" title="Esci">
                  <ArrowLeft size={16} />
                </button>
              )}
            </div>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B949E]" size={20} />
            <input
              data-testid="search-orders-input"
              type="text"
              placeholder="Cerca per nome, telefono, email o prodotto..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full bg-[#0D1117] border border-[#30363D] rounded-lg pl-10 pr-4 py-3 text-white placeholder-[#6E7681] focus:border-[#58A6FF] transition-colors"
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto p-4">
        {/* Status Legend */}
        {!loading && orders.length > 0 && (
          <div data-testid="status-legend" className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
            <button
              data-testid="filter-active"
              onClick={() => setStatusFilter("active")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors shrink-0 ${
                statusFilter === "active" 
                  ? "bg-[#58A6FF]/20 text-[#58A6FF] border border-[#58A6FF]" 
                  : "bg-[#21262D] text-[#8B949E] border border-[#30363D] hover:border-[#8B949E]"
              }`}
            >
              Attivi
              <span className="bg-[#30363D] px-1.5 py-0.5 rounded-full text-[11px]">{activeCount}</span>
            </button>
            {ORDER_STATUSES.map(status => (
              <button
                key={status}
                data-testid={`filter-${status.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => setStatusFilter(statusFilter === status ? "active" : status)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors shrink-0 ${
                  statusFilter === status
                    ? `border`
                    : "bg-[#21262D] text-[#8B949E] border border-[#30363D] hover:border-[#8B949E]"
                }`}
                style={statusFilter === status ? { 
                  backgroundColor: `${STATUS_COLORS[status]}20`, 
                  color: STATUS_COLORS[status],
                  borderColor: STATUS_COLORS[status]
                } : {}}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[status] }} />
                {status}
                <span className="bg-[#30363D] px-1.5 py-0.5 rounded-full text-[11px]">{statusCounts[status]}</span>
              </button>
            ))}
            <button
              data-testid="filter-all"
              onClick={() => setStatusFilter("all")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors shrink-0 ${
                statusFilter === "all"
                  ? "bg-[#A371F7]/20 text-[#A371F7] border border-[#A371F7]"
                  : "bg-[#21262D] text-[#8B949E] border border-[#30363D] hover:border-[#8B949E]"
              }`}
            >
              Tutti
              <span className="bg-[#30363D] px-1.5 py-0.5 rounded-full text-[11px]">{orders.length}</span>
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 size={48} className="text-[#58A6FF] animate-spin" />
            <p className="text-[#8B949E] mt-4">Caricamento ordini...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Package size={64} className="text-[#30363D] mb-4" />
            <p className="text-[#8B949E] text-lg mb-4">
              {statusFilter === "active" ? "Nessun ordine attivo" : statusFilter !== "all" ? `Nessun ordine con stato "${statusFilter}"` : "Nessun ordine trovato"}
            </p>
            <button
              data-testid="create-first-order-btn"
              onClick={onNewOrder}
              className="flex items-center gap-2 bg-[#238636] text-white px-6 py-3 rounded-lg hover:bg-[#2ea043] transition-colors"
            >
              <Plus size={20} />
              Crea il primo ordine
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="text-[#8B949E]">
                {filteredOrders.length} ordini {statusFilter === "active" ? "(attivi)" : statusFilter !== "all" ? `(${statusFilter})` : "(tutti)"}
              </p>
            </div>
            <div className="orders-grid">
              {filteredOrders.map(order => (
                <OrderCard 
                  key={order.id} 
                  order={order} 
                  onClick={() => onSelectOrder(order.id)}
                  onStatusChange={(orderId, newStatus) => {
                    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
                  }}
                />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

// Order Detail Page
const OrderDetail = ({ orderId, onBack, isNew = false, prefillCustomer = null, onNavigate }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("+39");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("Inserito");
  const [arrivalDate, setArrivalDate] = useState(getTodayISO());
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(getTodayISO());
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState(getTodayISO());
  
  // Modals
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showIva, setShowIva] = useState(false);

  // Fetch order
  useEffect(() => {
    if (!isNew && orderId) {
      fetchOrder();
    }
  }, [orderId, isNew]);

  // Prefill customer data when creating order from customer page
  useEffect(() => {
    if (isNew && prefillCustomer) {
      setFirstName(prefillCustomer.first_name || "");
      setLastName(prefillCustomer.last_name || "");
      setPhone(prefillCustomer.phone || "+39");
      setEmail(prefillCustomer.email || "");
    }
  }, [isNew, prefillCustomer]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      const data = response.data;
      setOrder(data);
      setFirstName(data.first_name);
      setLastName(data.last_name);
      setPhone(data.phone || "+39");
      setEmail(data.email || "");
      setStatus(data.status);
      setArrivalDate(formatDateForInput(data.arrival_date));
      setExpectedDeliveryDate(formatDateForInput(data.expected_delivery_date));
      setRequestedDeliveryDate(formatDateForInput(data.requested_delivery_date));
      setShowIva(!!data.iva);
    } catch (error) {
      toast.error("Errore nel caricamento ordine");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("Nome e cognome sono obbligatori");
      return;
    }

    setSaving(true);
    try {
      const orderData = {
        first_name: firstName,
        last_name: lastName,
        phone,
        email,
        status,
        arrival_date: arrivalDate,
        expected_delivery_date: expectedDeliveryDate,
        requested_delivery_date: requestedDeliveryDate
      };

      if (isNew) {
        const response = await axios.post(`${API}/orders`, orderData);
        toast.success("Ordine creato con successo");
        setOrder(response.data);
        // Redirect to edit mode - trigger a page refresh to show detail view
        window.location.href = `?order=${response.data.id}`;
      } else {
        await axios.put(`${API}/orders/${orderId}`, orderData);
        toast.success("Ordine salvato");
        fetchOrder();
      }
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Sei sicuro di voler eliminare questo ordine?")) return;
    
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success("Ordine eliminato");
      onBack();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handlePrint = () => {
    if (order) {
      exportOrderToPDF(order, showIva);
    }
  };

  const handleWhatsApp = () => {
    if (!phone || phone === "+39") {
      toast.error("Numero di telefono mancante");
      return;
    }
    const cleanPhone = phone.replace(/\s/g, "").replace(/^\+/, "");
    window.open(`https://wa.me/${cleanPhone}`, "_blank");
  };

  const handleSaveProduct = async (productData) => {
    try {
      if (selectedProduct?.id) {
        await axios.put(`${API}/orders/${order.id}/products/${selectedProduct.id}`, productData);
        toast.success("Prodotto aggiornato");
      } else {
        await axios.post(`${API}/orders/${order.id}/products`, productData);
        toast.success("Prodotto aggiunto");
      }
      fetchOrder();
      setShowProductModal(false);
    } catch (error) {
      toast.error("Errore nel salvataggio prodotto");
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await axios.delete(`${API}/orders/${order.id}/products/${productId}`);
      toast.success("Prodotto eliminato");
      fetchOrder();
    } catch (error) {
      toast.error("Errore nell'eliminazione prodotto");
    }
  };

  const calculateTotal = () => {
    return order?.products?.reduce((sum, p) => sum + (p.price * p.quantity), 0).toFixed(2) || "0.00";
  };

  if (loading) {
    return (
      <div className="app-container min-h-screen flex flex-col items-center justify-center">
        <Loader2 size={48} className="text-[#58A6FF] animate-spin" />
        <p className="text-[#8B949E] mt-4">Caricamento ordine...</p>
      </div>
    );
  }

  return (
    <div className="app-container min-h-screen">
      {/* Header */}
      <header className="app-header no-print">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <button
            data-testid="back-btn"
            onClick={onBack}
            className="p-2 text-white hover:bg-[#21262D] rounded-lg transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          
          <h1 className="text-lg font-semibold text-white">
            {isNew ? "Nuovo Ordine" : "Dettaglio Ordine"}
          </h1>
          
          <div className="flex items-center gap-2">
            {!isNew && order && (
              <>
                <button
                  data-testid="export-pdf-btn"
                  onClick={handlePrint}
                  className="p-2 text-[#A371F7] hover:bg-[#21262D] rounded-lg transition-colors"
                  title="Esporta PDF"
                >
                  <FileText size={22} />
                </button>
                <button
                  data-testid="print-btn"
                  onClick={() => window.print()}
                  className="p-2 text-[#58A6FF] hover:bg-[#21262D] rounded-lg transition-colors"
                  title="Stampa"
                >
                  <Printer size={22} />
                </button>
                <button
                  data-testid="delete-order-btn"
                  onClick={handleDelete}
                  className="p-2 text-red-400 hover:bg-[#21262D] rounded-lg transition-colors"
                >
                  <Trash2 size={22} />
                </button>
              </>
            )}
            <button
              data-testid="save-order-btn"
              onClick={handleSave}
              disabled={saving}
              className="p-2 text-[#238636] hover:bg-[#21262D] rounded-lg transition-colors"
            >
              {saving ? <Loader2 size={22} className="animate-spin" /> : <Save size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto p-4 space-y-4">
        {/* Status Badge */}
        {!isNew && (
          <div className="flex justify-center">
            <StatusBadge status={status} onClick={() => setShowStatusModal(true)} />
          </div>
        )}

        {/* Customer Info */}
        <section className="section-card">
          <h2 className="section-title flex items-center gap-2">
            <User size={18} />
            Informazioni Cliente
          </h2>
          
          <div className="space-y-4">
            {isNew ? (
              <CustomerAutocomplete
                firstName={firstName}
                lastName={lastName}
                setFirstName={setFirstName}
                setLastName={setLastName}
                setPhone={setPhone}
                setEmail={setEmail}
              />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="field-label">Nome *</label>
                  <input
                    data-testid="first-name-input"
                    type="text"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    placeholder="Inserisci il nome"
                    className="field-input"
                  />
                </div>
                <div>
                  <label className="field-label">Cognome *</label>
                  <input
                    data-testid="last-name-input"
                    type="text"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    placeholder="Inserisci il cognome"
                    className="field-input"
                  />
                </div>
              </div>
            )}
            
            <div>
              <label className="field-label flex items-center gap-2">
                <Phone size={14} />
                Numero di Cellulare
              </label>
              <input
                data-testid="phone-input"
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="+39 123 456 7890"
                className="field-input"
              />
            </div>
            
            <div>
              <label className="field-label flex items-center gap-2">
                <Mail size={14} />
                Email
              </label>
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="email@esempio.com"
                className="field-input"
              />
            </div>
          </div>
        </section>

        {/* Dates */}
        <section className="section-card">
          <h2 className="section-title flex items-center gap-2">
            <Calendar size={18} />
            Date
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="field-label flex items-center gap-2">
                <Calendar size={14} />
                Data di Arrivo
              </label>
              <input
                data-testid="arrival-date-input"
                type="date"
                value={arrivalDate}
                onChange={e => setArrivalDate(e.target.value)}
                className="field-input"
              />
            </div>
            
            <div>
              <label className="field-label flex items-center gap-2">
                <Clock size={14} />
                Data Consegna Prevista
              </label>
              <input
                data-testid="expected-date-input"
                type="date"
                value={expectedDeliveryDate}
                onChange={e => setExpectedDeliveryDate(e.target.value)}
                className="field-input"
              />
            </div>
            
            <div>
              <label className="field-label flex items-center gap-2">
                <Flag size={14} />
                Data Consegna Richiesta
              </label>
              <input
                data-testid="requested-date-input"
                type="date"
                value={requestedDeliveryDate}
                onChange={e => setRequestedDeliveryDate(e.target.value)}
                className="field-input"
              />
            </div>
          </div>
        </section>

        {/* Products - only show if order exists */}
        {order && (
          <section className="section-card">
            <div className="flex justify-between items-center mb-4">
              <h2 className="section-title mb-0 flex items-center gap-2">
                <Package size={18} />
                Prodotti
              </h2>
              <button
                data-testid="add-product-btn"
                onClick={() => { setSelectedProduct(null); setShowProductModal(true); }}
                className="flex items-center gap-1 text-[#238636] hover:text-[#2ea043] transition-colors"
              >
                <PlusCircle size={20} />
                <span className="font-semibold">Aggiungi</span>
              </button>
            </div>
            
            {order.products?.length === 0 ? (
              <div className="empty-state">
                <Package size={48} className="text-[#30363D] mb-3" />
                <p className="text-[#6E7681]">Nessun prodotto aggiunto</p>
              </div>
            ) : (
              <>
                {order.products?.map((product, index) => (
                  <div
                    key={product.id}
                    data-testid={`product-card-${product.id}`}
                    onClick={() => { setSelectedProduct(product); setShowProductModal(true); }}
                    className="product-card"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-semibold text-[#C9D1D9]">Prodotto {index + 1}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteProduct(product.id); }}
                        className="text-red-400 hover:text-red-300 p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    
                    {product.text_notes && (
                      <p className="text-[#8B949E] text-sm mb-2 line-clamp-2">{product.text_notes}</p>
                    )}
                    
                    {product.drawing_data && (
                      <div className="bg-white rounded-md h-20 mb-2 overflow-hidden">
                        <img 
                          src={product.drawing_data} 
                          alt="Disegno" 
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center pt-2 border-t border-[#30363D]">
                      <span className="text-[#8B949E] text-sm">Qtà: {product.quantity}</span>
                      <span className="text-[#58A6FF] font-semibold">
                        € {product.price.toFixed(2)} × {product.quantity} = € {(product.price * product.quantity).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
                
                <div className="total-container">
                  <div className="flex items-center gap-3">
                    <span className="text-[#C9D1D9] font-semibold">Totale Ordine</span>
                    <label data-testid="iva-toggle" className="flex items-center gap-1.5 cursor-pointer select-none" onClick={async () => {
                      const newVal = !showIva;
                      setShowIva(newVal);
                      if (order?.id) {
                        try { await axios.put(`${API}/orders/${order.id}`, { iva: newVal }); } catch {}
                      }
                    }}>
                      <div
                        className={`w-9 h-5 rounded-full transition-colors relative ${showIva ? 'bg-[#238636]' : 'bg-[#30363D]'}`}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${showIva ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </div>
                      <span className={`text-xs font-semibold ${showIva ? 'text-[#238636]' : 'text-[#8B949E]'}`}>IVA 22%</span>
                    </label>
                  </div>
                  <div className="text-right">
                    {showIva ? (
                      <div>
                        <span className="text-sm text-[#8B949E] block">Imponibile: € {calculateTotal()}</span>
                        <span className="text-sm text-[#8B949E] block">IVA 22%: € {(parseFloat(calculateTotal()) * 0.22).toFixed(2)}</span>
                        <span data-testid="total-with-iva" className="text-2xl font-bold text-[#238636]">€ {(parseFloat(calculateTotal()) * 1.22).toFixed(2)}</span>
                      </div>
                    ) : (
                      <span data-testid="total-no-iva" className="text-2xl font-bold text-[#238636]">€ {calculateTotal()}</span>
                    )}
                  </div>
                </div>
              </>
            )}
          </section>
        )}

        {/* Action Buttons */}
        {order && (
          <div className="flex flex-col gap-3 no-print">
            <div className="flex gap-3">
              <button
                data-testid="whatsapp-btn"
                onClick={handleWhatsApp}
                className="btn-whatsapp"
              >
                <MessageCircle size={24} />
                Contatta
              </button>
              
              <button
                data-testid="change-status-btn"
                onClick={() => setShowStatusModal(true)}
                className="btn-status"
                style={{ borderColor: STATUS_COLORS[status] }}
              >
                <span 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: STATUS_COLORS[status] }} 
                />
                <span style={{ color: STATUS_COLORS[status] }}>Stato Ordine</span>
              </button>
            </div>
            
            {onNavigate && (
              <button
                data-testid="go-to-customer-card-btn"
                onClick={() => onNavigate('customer-from-order', null, { first_name: firstName, last_name: lastName, phone, email })}
                className="w-full flex items-center justify-center gap-2 bg-[#21262D] text-[#A371F7] py-3 rounded-xl border border-[#A371F7]/30 hover:bg-[#A371F7]/10 transition-colors font-medium"
              >
                <UserCircle size={20} />
                Scheda Cliente
              </button>
            )}
          </div>
        )}

        {/* Save button for new orders */}
        {isNew && (
          <button
            data-testid="create-order-btn"
            onClick={handleSave}
            disabled={saving}
            className="w-full py-4 bg-[#238636] text-white rounded-xl font-semibold hover:bg-[#2ea043] transition-colors flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
            Crea Ordine
          </button>
        )}
      </main>

      {/* Modals */}
      <StatusModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        currentStatus={status}
        onSelect={async (newStatus) => {
          setStatus(newStatus);
          if (!isNew && order) {
            try {
              await axios.put(`${API}/orders/${order.id}`, { status: newStatus });
              toast.success(`Stato aggiornato: ${newStatus}`);
            } catch (error) {
              toast.error("Errore nell'aggiornamento stato");
            }
          }
        }}
      />

      <ProductModal
        isOpen={showProductModal}
        onClose={() => setShowProductModal(false)}
        product={selectedProduct}
        onSave={handleSaveProduct}
        onDelete={handleDeleteProduct}
      />
      
      <Toaster position="top-center" theme="dark" />
    </div>
  );
};

// ============ CUSTOMERS PAGE ============

// Customer Detail View (inline - replaces card grid)
const CustomerDetailView = ({ customer, orders, onBack, onSelectOrder, onNewOrder, onCustomerChanged }) => {
  const [pointsData, setPointsData] = useState(null);
  const [cardImage, setCardImage] = useState(null);
  const [loadingCard, setLoadingCard] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: customer.first_name,
    last_name: customer.last_name,
    phone: customer.phone || "+39",
    email: customer.email || ""
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const customerOrders = orders.filter(order =>
    order.first_name.toLowerCase() === customer.first_name.toLowerCase() &&
    order.last_name.toLowerCase() === customer.last_name.toLowerCase()
  );
  
  const totalSpent = customerOrders.reduce((sum, order) => {
    return sum + (order.products?.reduce((pSum, p) => pSum + (p.price * p.quantity), 0) || 0);
  }, 0);

  const completedOrders = customerOrders.filter(o => o.status === "Consegnato").length;
  const activeOrders = customerOrders.filter(o => o.status !== "Consegnato").length;

  // Fetch points
  useEffect(() => {
    if (customer.id) {
      axios.get(`${API}/customers/${customer.id}/points`)
        .then(res => setPointsData(res.data))
        .catch(() => {});
    }
  }, [customer.id]);

  const handleShowCard = async () => {
    setLoadingCard(true);
    try {
      const response = await axios.get(`${API}/customers/${customer.id}/card-base64`);
      setCardImage(response.data.image);
      setShowCard(true);
    } catch (error) {
      toast.error("Errore nella generazione tessera");
    } finally {
      setLoadingCard(false);
    }
  };

  const handleDownloadCard = () => {
    if (!cardImage) return;
    const link = document.createElement('a');
    link.href = cardImage;
    link.download = `tessera_${pointsData?.card_number || 'cliente'}.jpg`;
    link.click();
  };

  const handleShareCard = async () => {
    if (!cardImage) return;
    // Convert base64 to blob for sharing
    const response = await fetch(cardImage);
    const blob = await response.blob();
    const file = new File([blob], `tessera_${pointsData?.card_number || 'cliente'}.jpg`, { type: 'image/jpeg' });
    
    if (navigator.share && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: `Tessera Punti ${getCompanyInfo().name}` });
      } catch {}
    } else {
      handleDownloadCard();
    }
  };

  const handleWhatsApp = () => {
    if (!customer.phone || customer.phone === "+39") {
      toast.error("Numero di telefono mancante");
      return;
    }
    const cleanPhone = customer.phone.replace(/\s/g, "").replace(/^\+/, "");
    window.open(`https://wa.me/${cleanPhone}`, "_blank");
  };

  const handleSaveCustomer = async () => {
    if (!editForm.first_name.trim() || !editForm.last_name.trim()) {
      toast.error("Nome e cognome sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/customers/${customer.id}`, {
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim(),
        phone: editForm.phone.trim(),
        email: editForm.email.trim()
      });
      toast.success("Cliente aggiornato!");
      setEditing(false);
      if (onCustomerChanged) onCustomerChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore aggiornamento");
    } finally { setSaving(false); }
  };

  const handleDeleteCustomer = async () => {
    const msg = `Eliminare "${customer.first_name} ${customer.last_name}" e TUTTI i suoi dati?\n\nVerranno eliminati:\n- Anagrafica cliente\n- Tutti gli ordini associati\n- Storico punti e riscatti\n\nQuesta azione è IRREVERSIBILE!`;
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/customers/${customer.id}/full`);
      toast.success(res.data.message || "Cliente eliminato");
      if (onCustomerChanged) onCustomerChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    } finally { setDeleting(false); }
  };

  return (
    <div className="app-container min-h-screen">
      <header className="app-header">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button onClick={onBack} className="p-2 text-white hover:bg-[#21262D] rounded-lg transition-colors">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-lg font-semibold text-white">Scheda Cliente</h1>
          <div className="flex items-center gap-2">
            {!editing && (
              <button
                data-testid="edit-customer-btn"
                onClick={() => setEditing(true)}
                className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-3 py-2 rounded-lg hover:bg-[#30363D] transition-colors text-sm"
              >
                <Edit size={16} /> Modifica
              </button>
            )}
            <button
              data-testid="new-order-for-customer-btn"
              onClick={() => onNewOrder(customer)}
              className="flex items-center gap-1.5 bg-[#238636] text-white px-3 py-2 rounded-lg hover:bg-[#2ea043] transition-colors text-sm"
            >
              <Plus size={16} />
              <span className="hidden sm:inline">Nuovo Ordine</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Customer Info Card */}
        <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-5">
          {!editing ? (
            <>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-full bg-[#21262D] flex items-center justify-center flex-shrink-0">
                  <UserCircle size={36} className="text-[#58A6FF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-2xl font-bold text-white">
                    {customer.first_name} {customer.last_name}
                  </h2>
                  <div className="mt-2 space-y-1">
                    {customer.phone && customer.phone !== "+39" && (
                      <p className="text-[#C9D1D9] flex items-center gap-2">
                        <Phone size={16} className="text-[#8B949E]" />
                        {customer.phone}
                      </p>
                    )}
                    {customer.email && (
                      <p className="text-[#C9D1D9] flex items-center gap-2">
                        <Mail size={16} className="text-[#8B949E]" />
                        {customer.email}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick actions */}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={handleWhatsApp}
                  className="flex items-center gap-1.5 bg-[#25D366]/20 text-[#25D366] px-3 py-2 rounded-lg hover:bg-[#25D366]/30 transition-colors text-sm"
                >
                  <MessageCircle size={16} />
                  WhatsApp
                </button>
                <button
                  data-testid="delete-customer-btn"
                  onClick={handleDeleteCustomer}
                  disabled={deleting}
                  className="flex items-center gap-1.5 bg-red-500/10 text-red-400 px-3 py-2 rounded-lg hover:bg-red-500/20 transition-colors text-sm ml-auto"
                >
                  {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  Elimina Cliente
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Edit size={18} className="text-[#58A6FF]" />
                Modifica Anagrafica
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="field-label">Nome</label>
                  <input
                    data-testid="edit-first-name"
                    type="text"
                    value={editForm.first_name}
                    onChange={e => setEditForm({ ...editForm, first_name: e.target.value })}
                    className="field-input"
                  />
                </div>
                <div>
                  <label className="field-label">Cognome</label>
                  <input
                    data-testid="edit-last-name"
                    type="text"
                    value={editForm.last_name}
                    onChange={e => setEditForm({ ...editForm, last_name: e.target.value })}
                    className="field-input"
                  />
                </div>
                <div>
                  <label className="field-label">Telefono</label>
                  <input
                    data-testid="edit-phone"
                    type="tel"
                    value={editForm.phone}
                    onChange={e => setEditForm({ ...editForm, phone: e.target.value })}
                    className="field-input"
                  />
                </div>
                <div>
                  <label className="field-label">Email</label>
                  <input
                    data-testid="edit-email"
                    type="email"
                    value={editForm.email}
                    onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                    className="field-input"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setEditing(false)}
                  className="px-4 py-2.5 bg-[#21262D] text-[#C9D1D9] rounded-xl hover:bg-[#30363D] text-sm"
                >
                  Annulla
                </button>
                <button
                  data-testid="save-customer-btn"
                  onClick={handleSaveCustomer}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2.5 bg-[#238636] text-white rounded-xl hover:bg-[#2ea043] text-sm disabled:opacity-50"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Salva Modifiche
                </button>
              </div>
            </>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-4 text-center">
            <p className="text-[#8B949E] text-xs">Totale Ordini</p>
            <p className="text-2xl font-bold text-white">{customerOrders.length}</p>
          </div>
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-4 text-center">
            <p className="text-[#8B949E] text-xs">Totale Speso</p>
            <p className="text-2xl font-bold text-[#238636]">€ {totalSpent.toFixed(2)}</p>
          </div>
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-4 text-center">
            <p className="text-[#8B949E] text-xs">Attivi</p>
            <p className="text-2xl font-bold text-[#F0883E]">{activeOrders}</p>
          </div>
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-4 text-center">
            <p className="text-[#8B949E] text-xs">Consegnati</p>
            <p className="text-2xl font-bold text-[#58A6FF]">{completedOrders}</p>
          </div>
        </div>

        {/* Loyalty Points & Card */}
        {pointsData && (
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-5">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Award size={20} className="text-[#F0883E]" />
              Tessera Punti
            </h3>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="bg-[#0D1117] rounded-lg p-3 text-center">
                <p className="text-[#8B949E] text-xs">Punti</p>
                <p className="text-2xl font-bold text-[#F0883E]">{pointsData.points}</p>
              </div>
              <div className="bg-[#0D1117] rounded-lg p-3 text-center">
                <p className="text-[#8B949E] text-xs">Premi Disponibili</p>
                <p className="text-2xl font-bold text-[#238636]">{pointsData.rewards_available}</p>
              </div>
              <div className="bg-[#0D1117] rounded-lg p-3 text-center">
                <p className="text-[#8B949E] text-xs">Prossimo Premio</p>
                <p className="text-2xl font-bold text-[#58A6FF]">-{pointsData.points_to_next_reward}</p>
              </div>
              <div className="bg-[#0D1117] rounded-lg p-3 text-center">
                <p className="text-[#8B949E] text-xs">N° Tessera</p>
                <p className="text-sm font-bold text-[#C9D1D9] mt-1">{pointsData.card_number}</p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mb-4">
              <div className="flex justify-between text-xs text-[#8B949E] mb-1">
                <span>{pointsData.points % pointsData.reward_threshold} / {pointsData.reward_threshold} punti</span>
                <span>{pointsData.reward_discount}% sconto</span>
              </div>
              <div className="w-full bg-[#0D1117] rounded-full h-3">
                <div 
                  className="h-3 rounded-full transition-all"
                  style={{ 
                    width: `${((pointsData.points % pointsData.reward_threshold) / pointsData.reward_threshold) * 100}%`,
                    background: 'linear-gradient(90deg, #F0883E, #238636)'
                  }} 
                />
              </div>
            </div>

            {/* Card actions */}
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="show-card-btn"
                onClick={handleShowCard}
                disabled={loadingCard}
                className="flex items-center gap-1.5 bg-[#58A6FF]/20 text-[#58A6FF] px-4 py-2 rounded-lg hover:bg-[#58A6FF]/30 transition-colors text-sm"
              >
                {loadingCard ? <Loader2 size={16} className="animate-spin" /> : <QrCode size={16} />}
                Genera Tessera
              </button>
              <button
                data-testid="share-card-link-btn"
                onClick={() => {
                  const cardUrl = `${window.location.origin}?card=${pointsData.card_number}`;
                  if (navigator.share) {
                    navigator.share({
                      title: `Tessera Fedeltà - ${customer.first_name} ${customer.last_name}`,
                      text: `Ecco la tua tessera fedeltà ${getCompanyInfo().name}! Hai ${pointsData.points} punti.`,
                      url: cardUrl
                    }).catch(() => {});
                  } else {
                    navigator.clipboard.writeText(cardUrl).then(() => {
                      toast.success("Link copiato!");
                    });
                  }
                }}
                className="flex items-center gap-1.5 bg-[#F0883E]/20 text-[#F0883E] px-4 py-2 rounded-lg hover:bg-[#F0883E]/30 transition-colors text-sm border border-[#F0883E]/30"
              >
                <Share2 size={16} />
                Link Tessera
              </button>
              {cardImage && (
                <>
                  <button
                    data-testid="download-card-btn"
                    onClick={handleDownloadCard}
                    className="flex items-center gap-1.5 bg-[#238636]/20 text-[#238636] px-4 py-2 rounded-lg hover:bg-[#238636]/30 transition-colors text-sm"
                  >
                    <Download size={16} />
                    Scarica JPEG
                  </button>
                  <button
                    data-testid="share-card-btn"
                    onClick={handleShareCard}
                    className="flex items-center gap-1.5 bg-[#A371F7]/20 text-[#A371F7] px-4 py-2 rounded-lg hover:bg-[#A371F7]/30 transition-colors text-sm"
                  >
                    <MessageCircle size={16} />
                    Condividi Immagine
                  </button>
                </>
              )}
            </div>

            {/* Card preview */}
            {showCard && cardImage && (
              <div className="mt-4">
                <img 
                  data-testid="loyalty-card-image"
                  src={cardImage} 
                  alt="Tessera Punti" 
                  className="w-full max-w-md mx-auto rounded-xl shadow-lg border border-[#30363D]"
                />
              </div>
            )}
          </div>
        )}

        {/* Orders History */}
        <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-5">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Package size={20} className="text-[#58A6FF]" />
            Storico Ordini
          </h3>
          
          {customerOrders.length === 0 ? (
            <div className="text-center py-8">
              <Package size={48} className="mx-auto text-[#30363D] mb-3" />
              <p className="text-[#8B949E]">Nessun ordine per questo cliente</p>
            </div>
          ) : (
            <div className="space-y-3">
              {customerOrders.map(order => {
                const orderTotal = order.products?.reduce((sum, p) => sum + (p.price * p.quantity), 0) || 0;
                const displayTotal = order.iva ? orderTotal * 1.22 : orderTotal;
                return (
                  <div
                    key={order.id}
                    onClick={() => onSelectOrder(order.id)}
                    className="bg-[#0D1117] border border-[#30363D] rounded-xl p-4 cursor-pointer hover:border-[#58A6FF] transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-[#8B949E] text-xs mb-1">
                          Ordine #{order.id.slice(-8).toUpperCase()}
                        </p>
                        <StatusBadge status={order.status} small />
                      </div>
                      <ChevronRight size={20} className="text-[#8B949E]" />
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                      <div>
                        <p className="text-[#8B949E] text-xs">Data Arrivo</p>
                        <p className="text-[#C9D1D9]">{formatDateDisplay(order.arrival_date)}</p>
                      </div>
                      <div>
                        <p className="text-[#8B949E] text-xs">Consegna Prevista</p>
                        <p className="text-[#C9D1D9]">{formatDateDisplay(order.expected_delivery_date)}</p>
                      </div>
                    </div>
                    {order.products?.length > 0 && (
                      <div className="text-xs text-[#8B949E] mb-2">
                        {order.products.map(p => p.text_notes).filter(Boolean).join(', ') || `${order.products.length} prodotti`}
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-3 border-t border-[#30363D]">
                      <span className="text-[#8B949E] text-sm">{order.products?.length || 0} prodotti</span>
                      <span className="text-[#238636] font-bold">
                        € {displayTotal.toFixed(2)}
                        {order.iva && <span className="text-xs text-[#8B949E] font-normal ml-1">(IVA incl.)</span>}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// Customers List Page
const CustomersList = ({ onNavigate, onSelectOrder, autoSelectCustomer = null, onLogout }) => {
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [sortBy, setSortBy] = useState("name_asc");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, ordersRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/orders`)
      ]);
      setCustomers(customersRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  // Auto-select customer when navigating from order detail
  useEffect(() => {
    if (!loading && autoSelectCustomer && !selectedCustomer) {
      const match = getUniqueCustomers().find(c =>
        c.first_name.toLowerCase() === autoSelectCustomer.first_name?.toLowerCase() &&
        c.last_name.toLowerCase() === autoSelectCustomer.last_name?.toLowerCase()
      );
      if (match) setSelectedCustomer(match);
    }
  }, [loading, autoSelectCustomer]);

  const getUniqueCustomers = () => {
    const customerMap = new Map();
    customers.forEach(c => {
      const key = `${c.first_name.toLowerCase()}_${c.last_name.toLowerCase()}`;
      customerMap.set(key, { id: c.id, first_name: c.first_name, last_name: c.last_name, phone: c.phone, email: c.email });
    });
    orders.forEach(order => {
      const key = `${order.first_name.toLowerCase()}_${order.last_name.toLowerCase()}`;
      if (!customerMap.has(key)) {
        customerMap.set(key, { id: key, first_name: order.first_name, last_name: order.last_name, phone: order.phone, email: order.email });
      } else {
        const existing = customerMap.get(key);
        if ((!existing.phone || existing.phone === '+39') && order.phone && order.phone !== '+39') existing.phone = order.phone;
        if (!existing.email && order.email) existing.email = order.email;
      }
    });
    return Array.from(customerMap.values());
  };

  const getCustomerStats = (customer) => {
    const customerOrders = orders.filter(order =>
      order.first_name.toLowerCase() === customer.first_name.toLowerCase() &&
      order.last_name.toLowerCase() === customer.last_name.toLowerCase()
    );
    const totalSpent = customerOrders.reduce((sum, order) => {
      return sum + (order.products?.reduce((pSum, p) => pSum + (p.price * p.quantity), 0) || 0);
    }, 0);
    return { ordersCount: customerOrders.length, totalSpent };
  };

  const uniqueCustomers = getUniqueCustomers();
  const filteredCustomers = searchQuery.length >= 1
    ? uniqueCustomers.filter(c =>
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.phone?.includes(searchQuery) ||
        c.email?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : uniqueCustomers;

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    switch (sortBy) {
      case "name_asc":
        return a.first_name.localeCompare(b.first_name, 'it');
      case "name_desc":
        return b.first_name.localeCompare(a.first_name, 'it');
      case "surname_asc":
        return a.last_name.localeCompare(b.last_name, 'it');
      case "surname_desc":
        return b.last_name.localeCompare(a.last_name, 'it');
      case "orders":
        return getCustomerStats(b).ordersCount - getCustomerStats(a).ordersCount;
      case "spent":
        return getCustomerStats(b).totalSpent - getCustomerStats(a).totalSpent;
      default:
        return a.first_name.localeCompare(b.first_name, 'it');
    }
  });

  const handleNewOrderForCustomer = (customer) => {
    // Navigate to new order with pre-filled customer data
    onNavigate('new-order-customer', null, customer);
  };

  if (loading) {
    return (
      <div className="app-container min-h-screen flex flex-col items-center justify-center">
        <Loader2 size={48} className="text-[#58A6FF] animate-spin" />
        <p className="text-[#8B949E] mt-4">Caricamento clienti...</p>
      </div>
    );
  }

  // Show detail view if a customer is selected
  if (selectedCustomer) {
    return (
      <CustomerDetailView
        customer={selectedCustomer}
        orders={orders}
        onBack={() => setSelectedCustomer(null)}
        onSelectOrder={onSelectOrder}
        onNewOrder={handleNewOrderForCustomer}
        onCustomerChanged={() => { setSelectedCustomer(null); fetchData(); }}
      />
    );
  }

  return (
    <div className="app-container min-h-screen">
      <header className="app-header">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                <Users size={24} className="text-[#A371F7]" />
                Clienti
              </h1>
              <button
                data-testid="go-to-dashboard-from-customers-btn"
                onClick={() => onNavigate('dashboard')}
                className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-2.5 py-1.5 rounded-lg hover:bg-[#30363D] transition-colors text-xs sm:text-sm"
              >
                <BarChart3 size={14} />
                <span>Dashboard</span>
              </button>
              <button
                data-testid="go-to-orders-from-customers-btn"
                onClick={() => onNavigate('list')}
                className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-2.5 py-1.5 rounded-lg hover:bg-[#30363D] transition-colors text-xs sm:text-sm"
              >
                <Package size={14} />
                <span>Ordini</span>
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[#8B949E] text-sm">{sortedCustomers.length} clienti</span>
              {onLogout && (
                <button onClick={onLogout} className="flex items-center gap-1.5 bg-red-500/10 text-red-400 px-2.5 py-1.5 rounded-lg hover:bg-red-500/20 transition-colors" title="Esci">
                  <ArrowLeft size={14} />
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B949E]" size={20} />
              <input
                data-testid="search-customers-input"
                type="text"
                placeholder="Cerca per nome, telefono o email..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full bg-[#0D1117] border border-[#30363D] rounded-lg pl-10 pr-4 py-3 text-white placeholder-[#6E7681] focus:border-[#58A6FF] transition-colors"
              />
            </div>
            <select
              data-testid="sort-customers"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="bg-[#0D1117] border border-[#30363D] rounded-lg px-4 py-3 text-white focus:border-[#58A6FF] transition-colors min-w-[170px] text-sm"
            >
              <option value="name_asc">Nome A → Z</option>
              <option value="name_desc">Nome Z → A</option>
              <option value="surname_asc">Cognome A → Z</option>
              <option value="surname_desc">Cognome Z → A</option>
              <option value="orders">Più ordini</option>
              <option value="spent">Più speso</option>
            </select>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4">
        {sortedCustomers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Users size={64} className="text-[#30363D] mb-4" />
            <p className="text-[#8B949E] text-lg mb-2">
              {searchQuery ? "Nessun cliente trovato" : "Nessun cliente"}
            </p>
            <p className="text-[#6E7681] text-sm">
              I clienti verranno aggiunti automaticamente quando crei ordini
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sortedCustomers.map(customer => {
              const stats = getCustomerStats(customer);
              return (
                <div
                  key={customer.id}
                  data-testid={`customer-card-${customer.id}`}
                  onClick={() => setSelectedCustomer(customer)}
                  className="bg-[#161B22] border border-[#30363D] rounded-xl p-4 hover:border-[#58A6FF] transition-colors cursor-pointer"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-full bg-[#21262D] flex items-center justify-center flex-shrink-0">
                      <UserCircle size={24} className="text-[#58A6FF]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-semibold truncate">
                        {customer.first_name} {customer.last_name}
                      </h3>
                      {customer.phone && customer.phone !== "+39" && (
                        <p className="text-[#8B949E] text-sm truncate">{customer.phone}</p>
                      )}
                      {customer.email && (
                        <p className="text-[#8B949E] text-xs truncate">{customer.email}</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-[#30363D] flex items-center justify-between">
                    <span className="text-[#8B949E] text-sm">{stats.ordersCount} ordini</span>
                    <span className="text-[#238636] font-semibold">€ {stats.totalSpent.toFixed(2)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

// ============ INVENTORY PAGE ============

const InventoryItemModal = ({ isOpen, onClose, item, onSave }) => {
  const [name, setName] = useState(item?.name || "");
  const [quantity, setQuantity] = useState(String(item?.quantity ?? 0));
  const [costPrice, setCostPrice] = useState(String(item?.cost_price ?? ""));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setName(item.name || "");
      setQuantity(String(item.quantity ?? 0));
      setCostPrice(String(item.cost_price ?? ""));
    } else {
      setName("");
      setQuantity("1");
      setCostPrice("");
    }
  }, [item, isOpen]);

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Il nome è obbligatorio"); return; }
    setSaving(true);
    await onSave({
      name: name.trim(),
      quantity: parseInt(quantity) || 0,
      cost_price: parseFloat(costPrice) || 0
    });
    setSaving(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 modal-overlay" onClick={onClose}>
      <div className="bg-[#161B22] w-full max-w-md rounded-2xl animate-fadeIn" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-5 border-b border-[#30363D]">
          <h3 className="text-xl font-bold text-white">
            {item?.id ? "Modifica Articolo" : "Nuovo Articolo"}
          </h3>
          <button onClick={onClose} className="text-[#8B949E] hover:text-white"><X size={24} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="field-label">Nome Articolo *</label>
            <input
              data-testid="inventory-name-input"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="es. Portachiavi, Targa, Cornice..."
              className="field-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="field-label">Quantità</label>
              <input
                data-testid="inventory-quantity-input"
                type="number"
                min="0"
                inputMode="numeric"
                value={quantity}
                onChange={e => setQuantity(e.target.value)}
                onFocus={e => e.target.select()}
                className="field-input"
              />
            </div>
            <div>
              <label className="field-label">Prezzo di Costo (€)</label>
              <input
                data-testid="inventory-cost-input"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={costPrice}
                onChange={e => setCostPrice(e.target.value)}
                onFocus={e => e.target.select()}
                className="field-input"
              />
            </div>
          </div>
          <div className="bg-[#21262D] rounded-lg p-4 flex justify-between items-center">
            <span className="text-[#8B949E]">Valore Totale</span>
            <span className="text-xl font-bold text-[#238636]">
              € {((parseFloat(costPrice) || 0) * (parseInt(quantity) || 0)).toFixed(2)}
            </span>
          </div>
        </div>
        <div className="p-5 border-t border-[#30363D] flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-3 bg-[#21262D] text-[#C9D1D9] rounded-xl hover:bg-[#30363D] transition-colors">
            Annulla
          </button>
          <button
            data-testid="inventory-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-3 bg-[#238636] text-white rounded-xl hover:bg-[#2ea043] transition-colors flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
            Salva
          </button>
        </div>
      </div>
    </div>
  );
};

const InventoryPage = ({ onNavigate, onLogout }) => {
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/inventory`);
      setItems(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento magazzino");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (selectedItem?.id) {
        await axios.put(`${API}/inventory/${selectedItem.id}`, data);
        toast.success("Articolo aggiornato");
      } else {
        await axios.post(`${API}/inventory`, data);
        toast.success("Articolo aggiunto");
      }
      fetchItems();
      setShowModal(false);
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm("Eliminare questo articolo?")) return;
    try {
      await axios.delete(`${API}/inventory/${itemId}`);
      toast.success("Articolo eliminato");
      fetchItems();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const filteredItems = searchQuery.length >= 1
    ? items.filter(i => i.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : items;

  const totalItems = filteredItems.reduce((sum, i) => sum + i.quantity, 0);
  const totalValue = filteredItems.reduce((sum, i) => sum + (i.cost_price * i.quantity), 0);

  if (loading) {
    return (
      <div className="app-container min-h-screen flex flex-col items-center justify-center">
        <Loader2 size={48} className="text-[#58A6FF] animate-spin" />
        <p className="text-[#8B949E] mt-4">Caricamento magazzino...</p>
      </div>
    );
  }

  return (
    <div className="app-container min-h-screen">
      <header className="app-header">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                <Warehouse size={24} className="text-[#F0883E]" />
                Magazzino
              </h1>
              <button
                data-testid="go-to-dashboard-from-inventory-btn"
                onClick={() => onNavigate('dashboard')}
                className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-2.5 py-1.5 rounded-lg hover:bg-[#30363D] transition-colors text-xs sm:text-sm"
              >
                <BarChart3 size={14} />
                <span>Dashboard</span>
              </button>
              <button
                data-testid="go-to-orders-from-inventory-btn"
                onClick={() => onNavigate('list')}
                className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-2.5 py-1.5 rounded-lg hover:bg-[#30363D] transition-colors text-xs sm:text-sm"
              >
                <Package size={14} />
                <span>Ordini</span>
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="add-inventory-btn"
                onClick={() => { setSelectedItem(null); setShowModal(true); }}
                className="flex items-center gap-1.5 bg-[#238636] text-white px-3 py-2 rounded-lg hover:bg-[#2ea043] transition-colors text-sm"
              >
                <Plus size={18} />
                <span className="hidden sm:inline">Nuovo Articolo</span>
              </button>
              {onLogout && (
                <button onClick={onLogout} className="flex items-center gap-1.5 bg-red-500/10 text-red-400 px-2.5 py-2 rounded-lg hover:bg-red-500/20 transition-colors" title="Esci">
                  <ArrowLeft size={16} />
                </button>
              )}
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B949E]" size={20} />
            <input
              data-testid="search-inventory-input"
              type="text"
              placeholder="Cerca articolo..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full bg-[#0D1117] border border-[#30363D] rounded-lg pl-10 pr-4 py-3 text-white placeholder-[#6E7681] focus:border-[#58A6FF] transition-colors"
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4">
        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-4 text-center">
            <p className="text-[#8B949E] text-xs">Articoli</p>
            <p className="text-2xl font-bold text-white">{filteredItems.length}</p>
          </div>
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-4 text-center">
            <p className="text-[#8B949E] text-xs">Pezzi Totali</p>
            <p className="text-2xl font-bold text-[#58A6FF]">{totalItems}</p>
          </div>
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-4 text-center col-span-2 sm:col-span-1">
            <p className="text-[#8B949E] text-xs">Valore Magazzino</p>
            <p className="text-2xl font-bold text-[#238636]">€ {totalValue.toFixed(2)}</p>
          </div>
        </div>

        {filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Warehouse size={64} className="text-[#30363D] mb-4" />
            <p className="text-[#8B949E] text-lg mb-2">
              {searchQuery ? "Nessun articolo trovato" : "Magazzino vuoto"}
            </p>
            <p className="text-[#6E7681] text-sm mb-4">Aggiungi il primo articolo al magazzino</p>
            <button
              onClick={() => { setSelectedItem(null); setShowModal(true); }}
              className="flex items-center gap-2 bg-[#238636] text-white px-6 py-3 rounded-lg hover:bg-[#2ea043] transition-colors"
            >
              <Plus size={20} />
              Aggiungi Articolo
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filteredItems.map(item => (
              <div
                key={item.id}
                data-testid={`inventory-item-${item.id}`}
                className="bg-[#161B22] border border-[#30363D] rounded-xl p-4 hover:border-[#F0883E] transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-white font-semibold text-lg truncate flex-1">{item.name}</h3>
                  <div className="flex gap-1 ml-2 flex-shrink-0">
                    <button
                      data-testid={`edit-item-${item.id}`}
                      onClick={() => { setSelectedItem(item); setShowModal(true); }}
                      className="p-1.5 text-[#58A6FF] hover:bg-[#21262D] rounded-lg transition-colors"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      data-testid={`delete-item-${item.id}`}
                      onClick={() => handleDelete(item.id)}
                      className="p-1.5 text-red-400 hover:bg-[#21262D] rounded-lg transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[#8B949E] text-xs">Quantità</p>
                    <p className={`text-lg font-bold ${item.quantity <= 0 ? 'text-red-400' : item.quantity <= 5 ? 'text-[#F0883E]' : 'text-white'}`}>
                      {item.quantity}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#8B949E] text-xs">Prezzo Costo</p>
                    <p className="text-lg font-bold text-[#C9D1D9]">€ {item.cost_price.toFixed(2)}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-[#30363D] flex justify-between items-center">
                  <span className="text-[#8B949E] text-sm">Valore</span>
                  <span className="text-[#238636] font-semibold">€ {(item.cost_price * item.quantity).toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <InventoryItemModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        item={selectedItem}
        onSave={handleSave}
      />
    </div>
  );
};

// ============ QR SCANNER PAGE ============

const QrScannerPage = ({ onNavigate, onLogout }) => {
  // "idle" | "scanning" | "loading" | "found" | "error"
  const [phase, setPhase] = useState("scanning");
  const [customerData, setCustomerData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [scanText, setScanText] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [redeemSuccess, setRedeemSuccess] = useState(null);
  const scannerInitialized = useRef(false);
  const html5QrCodeRef = useRef(null);

  // Cleanup scanner
  const stopScanner = useCallback(() => {
    if (html5QrCodeRef.current) {
      try {
        html5QrCodeRef.current.stop().catch(() => {});
      } catch {}
      try {
        html5QrCodeRef.current.clear();
      } catch {}
      html5QrCodeRef.current = null;
    }
    scannerInitialized.current = false;
  }, []);

  // Lookup customer from QR data
  const lookupCustomer = useCallback(async (qrData) => {
    setPhase("loading");
    setScanText(qrData);
    try {
      const response = await axios.post(`${API}/scan`, { qr_data: qrData });
      if (response.data && response.data.first_name) {
        setCustomerData(response.data);
        setPhase("found");
      } else {
        setErrorMsg("Risposta non valida dal server");
        setPhase("error");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Cliente non trovato. QR code non valido.";
      setErrorMsg(msg);
      setPhase("error");
    }
  }, []);

  // Start scanner
  useEffect(() => {
    if (phase !== "scanning") return;
    if (scannerInitialized.current) return;

    let mounted = true;
    scannerInitialized.current = true;

    const initScanner = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        
        // Wait for DOM element
        await new Promise(r => setTimeout(r, 500));
        const el = document.getElementById("qr-reader-box");
        if (!el || !mounted) return;

        const scanner = new Html5Qrcode("qr-reader-box");
        html5QrCodeRef.current = scanner;

        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 220, height: 220 }, aspectRatio: 1 },
          (decodedText) => {
            if (!mounted) return;
            stopScanner();
            lookupCustomer(decodedText);
          },
          () => {} // ignore errors during scanning
        );
      } catch (err) {
        if (mounted) {
          setErrorMsg("Impossibile accedere alla fotocamera. Verifica i permessi del browser.");
          setPhase("error");
        }
      }
    };

    initScanner();

    return () => {
      mounted = false;
      stopScanner();
    };
  }, [phase, stopScanner, lookupCustomer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopScanner();
  }, [stopScanner]);

  const handleRescan = () => {
    stopScanner();
    setCustomerData(null);
    setErrorMsg("");
    setScanText("");
    setPhase("scanning");
  };

  const handleManualInput = async () => {
    const input = window.prompt("Inserisci il numero tessera (es. SCG-XXXXXXXX):");
    if (!input) return;
    stopScanner();
    lookupCustomer(input.trim().toUpperCase());
  };

  const handleRedeem = async () => {
    if (!customerData) return;
    if (!window.confirm(`Riscattare ${customerData.reward_threshold} punti per uno sconto del ${customerData.reward_discount}%?`)) return;
    
    setRedeeming(true);
    setRedeemSuccess(null);
    try {
      const response = await axios.post(`${API}/customers/${customerData.customer_id}/redeem`);
      setRedeemSuccess(response.data);
      // Refresh customer data
      const updated = await axios.post(`${API}/scan`, { qr_data: scanText });
      setCustomerData(updated.data);
      toast.success(`Premio riscattato! Sconto ${response.data.discount_percent}%`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore nel riscatto";
      toast.error(msg);
    } finally {
      setRedeeming(false);
    }
  };

  // Header (always shown)
  const header = (
    <header className="app-header">
      <div className="max-w-4xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2">
          <QrCode size={24} className="text-[#F0883E]" />
          <h1 className="text-xl font-bold text-white">Scanner Tessera</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="go-to-dashboard-from-scanner"
            onClick={() => onNavigate('dashboard')}
            className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-2.5 py-1.5 rounded-lg hover:bg-[#30363D] transition-colors text-xs sm:text-sm"
          >
            <BarChart3 size={14} />
            <span className="hidden sm:inline">Dashboard</span>
          </button>
          <button
            data-testid="go-to-orders-from-scanner"
            onClick={() => onNavigate('list')}
            className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-2.5 py-1.5 rounded-lg hover:bg-[#30363D] transition-colors text-xs sm:text-sm"
          >
            <Package size={14} />
            <span className="hidden sm:inline">Ordini</span>
          </button>
          {onLogout && (
            <button onClick={onLogout} className="flex items-center gap-1.5 bg-red-500/10 text-red-400 px-2.5 py-1.5 rounded-lg hover:bg-red-500/20 transition-colors" title="Esci">
              <ArrowLeft size={14} />
            </button>
          )}
        </div>
      </div>
    </header>
  );

  return (
    <div className="app-container min-h-screen">
      {header}

      <main className="max-w-lg mx-auto p-4 space-y-4">

        {/* SCANNING PHASE */}
        {phase === "scanning" && (
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl overflow-hidden">
            <div className="p-4 border-b border-[#30363D] text-center">
              <p className="text-white font-semibold">Inquadra il QR Code della tessera</p>
              <p className="text-[#8B949E] text-sm mt-1">Punta la fotocamera sul QR code del cliente</p>
            </div>
            <div id="qr-reader-box" style={{ minHeight: '300px', width: '100%' }} />
            <div className="p-4 text-center">
              <button
                data-testid="manual-input-btn"
                onClick={handleManualInput}
                className="text-[#58A6FF] text-sm hover:underline"
              >
                Inserisci numero tessera manualmente
              </button>
            </div>
          </div>
        )}

        {/* LOADING PHASE */}
        {phase === "loading" && (
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-8 flex flex-col items-center">
            <Loader2 size={48} className="text-[#58A6FF] animate-spin" />
            <p className="text-[#8B949E] mt-4">Ricerca cliente...</p>
            {scanText && <p className="text-[#6E7681] text-xs mt-2 break-all">{scanText}</p>}
          </div>
        )}

        {/* ERROR PHASE */}
        {phase === "error" && (
          <div className="bg-[#161B22] border border-red-500/50 rounded-xl p-5 text-center">
            <X size={48} className="mx-auto text-red-400 mb-3" />
            <p className="text-red-400 font-semibold mb-2">{errorMsg}</p>
            {scanText && <p className="text-[#6E7681] text-xs mb-4 break-all">QR: {scanText}</p>}
            <div className="flex gap-2 justify-center mt-4">
              <button
                data-testid="rescan-after-error-btn"
                onClick={handleRescan}
                className="flex items-center gap-1.5 bg-[#58A6FF]/20 text-[#58A6FF] px-4 py-2 rounded-lg hover:bg-[#58A6FF]/30 transition-colors text-sm"
              >
                <QrCode size={16} />
                Riprova Scansione
              </button>
              <button
                onClick={handleManualInput}
                className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-4 py-2 rounded-lg hover:bg-[#30363D] transition-colors text-sm"
              >
                Inserisci Manualmente
              </button>
            </div>
          </div>
        )}

        {/* FOUND PHASE */}
        {phase === "found" && customerData && (
          <div className="space-y-4 animate-fadeIn">
            <div className="bg-[#161B22] border border-[#238636] rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-[#238636] animate-pulse" />
                <span className="text-[#238636] font-semibold text-sm">Cliente Trovato</span>
              </div>
              
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-[#21262D] flex items-center justify-center flex-shrink-0">
                  <UserCircle size={32} className="text-[#58A6FF]" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-white">
                    {customerData.first_name} {customerData.last_name}
                  </h2>
                  {customerData.phone && customerData.phone !== "+39" && (
                    <p className="text-[#8B949E] text-sm">{customerData.phone}</p>
                  )}
                  <p className="text-[#58A6FF] text-sm mt-1">{customerData.card_number}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-[#0D1117] rounded-xl p-4 text-center">
                  <p className="text-[#8B949E] text-xs">Punti</p>
                  <p className="text-3xl font-bold text-[#F0883E]">{customerData.points}</p>
                </div>
                <div className="bg-[#0D1117] rounded-xl p-4 text-center">
                  <p className="text-[#8B949E] text-xs">Premi</p>
                  <p className="text-3xl font-bold text-[#238636]">{customerData.rewards_available}</p>
                </div>
                <div className="bg-[#0D1117] rounded-xl p-4 text-center">
                  <p className="text-[#8B949E] text-xs">Ordini</p>
                  <p className="text-3xl font-bold text-[#58A6FF]">{customerData.total_orders}</p>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex justify-between text-xs text-[#8B949E] mb-1">
                  <span>{customerData.points % customerData.reward_threshold} / {customerData.reward_threshold}</span>
                  <span>→ {customerData.reward_discount}% sconto</span>
                </div>
                <div className="w-full bg-[#0D1117] rounded-full h-4">
                  <div 
                    className="h-4 rounded-full transition-all flex items-center justify-center"
                    style={{ 
                      width: `${Math.max(((customerData.points % customerData.reward_threshold) / customerData.reward_threshold) * 100, 5)}%`,
                      background: 'linear-gradient(90deg, #F0883E, #238636)'
                    }}
                  >
                    <span className="text-[10px] font-bold text-white drop-shadow">
                      {((customerData.points % customerData.reward_threshold) / customerData.reward_threshold * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
                <p className="text-[#8B949E] text-xs mt-1 text-center">
                  Mancano <span className="text-[#F0883E] font-semibold">{customerData.points_to_next_reward}</span> punti al prossimo premio
                </p>
              </div>

              {customerData.rewards_available > 0 && (
                <div className="bg-[#238636]/20 border border-[#238636] rounded-xl p-4 text-center mb-4">
                  <Star size={24} className="mx-auto text-[#238636] mb-2" />
                  <p className="text-[#238636] font-bold text-lg">
                    {customerData.rewards_available} Premio{customerData.rewards_available > 1 ? 'i' : ''} Disponibil{customerData.rewards_available > 1 ? 'i' : 'e'}!
                  </p>
                  <p className="text-[#8B949E] text-sm mb-3">Sconto {customerData.reward_discount}% applicabile</p>
                  <button
                    data-testid="redeem-reward-btn"
                    onClick={handleRedeem}
                    disabled={redeeming}
                    className="flex items-center justify-center gap-2 mx-auto bg-[#238636] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#2ea043] transition-colors"
                  >
                    {redeeming ? <Loader2 size={20} className="animate-spin" /> : <Gift size={20} />}
                    Riscatta Sconto {customerData.reward_discount}%
                  </button>
                </div>
              )}

              {redeemSuccess && (
                <div className="bg-[#A371F7]/20 border border-[#A371F7] rounded-xl p-4 text-center mb-4 animate-fadeIn">
                  <Gift size={24} className="mx-auto text-[#A371F7] mb-2" />
                  <p className="text-[#A371F7] font-bold">Premio Riscattato!</p>
                  <p className="text-white text-lg font-bold">Sconto {redeemSuccess.discount_percent}%</p>
                  <p className="text-[#8B949E] text-sm mt-1">{redeemSuccess.points_used} punti utilizzati • Rimangono {redeemSuccess.remaining_points} punti</p>
                </div>
              )}

              <div className="bg-[#0D1117] rounded-lg p-3 flex justify-between items-center">
                <span className="text-[#8B949E] text-sm">Totale Speso</span>
                <span className="text-[#238636] font-bold text-lg">€ {customerData.total_spent.toFixed(2)}</span>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <button
                data-testid="new-order-from-scan-btn"
                onClick={() => onNavigate('new-order-customer', null, {
                  first_name: customerData.first_name,
                  last_name: customerData.last_name,
                  phone: customerData.phone,
                  email: customerData.email
                })}
                className="w-full flex items-center justify-center gap-2 bg-[#238636] text-white py-3 rounded-xl font-semibold hover:bg-[#2ea043] transition-colors"
              >
                <Plus size={20} />
                Nuovo Ordine per {customerData.first_name}
              </button>
              <button
                data-testid="rescan-btn"
                onClick={handleRescan}
                className="w-full flex items-center justify-center gap-2 bg-[#21262D] text-[#C9D1D9] py-3 rounded-xl hover:bg-[#30363D] transition-colors"
              >
                <QrCode size={20} />
                Scansiona un'altra tessera
              </button>
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

// ============ PUBLIC LOYALTY CARD PAGE ============

const PublicCardPage = () => {
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const cardNumber = new URLSearchParams(window.location.search).get("card");

  useEffect(() => {
    if (!cardNumber) { setError("Numero tessera mancante"); setLoading(false); return; }
    
    // Swap manifest to card-specific one
    const existingManifest = document.querySelector('link[rel="manifest"]');
    if (existingManifest) existingManifest.remove();
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = `${API}/card-manifest/${cardNumber}`;
    document.head.appendChild(link);

    // Update page title
    document.title = "Tessera Fedeltà";

    axios.get(`${API}/card/${cardNumber}`)
      .then(res => {
        setCardData(res.data);
        document.title = `Tessera ${res.data.first_name}`;
      })
      .catch(() => setError("Tessera non trovata"))
      .finally(() => setLoading(false));

    // Catch "beforeinstallprompt" for Android install button
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [cardNumber]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
  };

  const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone === true;

  const progressPercent = cardData
    ? ((cardData.points % cardData.reward_threshold) / cardData.reward_threshold) * 100
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D1117] flex flex-col items-center justify-center">
        <Loader2 size={48} className="text-[#58A6FF] animate-spin" />
        <p className="text-[#8B949E] mt-4">Caricamento tessera...</p>
      </div>
    );
  }

  if (error || !cardData) {
    return (
      <div className="min-h-screen bg-[#0D1117] flex flex-col items-center justify-center p-6">
        <X size={64} className="text-red-400 mb-4" />
        <p className="text-red-400 text-xl font-bold mb-2">{error || "Errore"}</p>
        <p className="text-[#8B949E]">Verifica il link della tessera</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D1117] flex flex-col items-center p-4 pt-8">
      {/* Card */}
      <div className="w-full max-w-sm">
        {/* Top accent */}
        <div className="h-1.5 bg-[#58A6FF] rounded-t-2xl" />
        
        <div className="bg-[#161B22] border border-[#30363D] border-t-0 rounded-b-2xl p-6 space-y-5">
          {/* Shop Header */}
          <div className="text-center">
            {cardData.logo && (
              <img src={`${BACKEND_URL}${cardData.logo}`} alt="" className="w-14 h-14 mx-auto mb-2 rounded-xl object-contain" />
            )}
            <h1 className="text-2xl font-bold text-white">{cardData.shop_name}</h1>
            <p className="text-[#8B949E] text-sm mt-1">{cardData.shop_tagline}</p>
          </div>

          <div className="h-px bg-[#30363D]" />

          {/* Customer */}
          <div className="text-center">
            <p className="text-[#58A6FF] text-xs font-medium tracking-widest">TESSERA FEDELTÀ</p>
            <h2 className="text-xl font-bold text-white mt-1">
              {cardData.first_name} {cardData.last_name}
            </h2>
            <p className="text-[#58A6FF] text-sm mt-1">{cardData.card_number}</p>
          </div>

          <div className="h-px bg-[#30363D]" />

          {/* Points */}
          <div className="text-center">
            <p className="text-[#8B949E] text-xs tracking-widest mb-1">PUNTI ACCUMULATI</p>
            <p className="text-5xl font-bold text-[#238636]">{cardData.points}</p>
          </div>

          {/* Progress bar */}
          <div>
            <div className="flex justify-between text-xs text-[#8B949E] mb-1.5">
              <span>{cardData.points % cardData.reward_threshold} / {cardData.reward_threshold}</span>
              <span>{cardData.reward_discount}% sconto</span>
            </div>
            <div className="w-full bg-[#0D1117] rounded-full h-3.5">
              <div
                className="h-3.5 rounded-full transition-all"
                style={{
                  width: `${Math.max(progressPercent, 3)}%`,
                  background: 'linear-gradient(90deg, #F0883E, #238636)'
                }}
              />
            </div>
            <p className="text-[#8B949E] text-xs mt-1.5 text-center">
              {cardData.points_to_next_reward > 0
                ? <>Mancano <span className="text-[#F0883E] font-semibold">{cardData.points_to_next_reward}</span> punti al prossimo premio</>
                : <span className="text-[#238636] font-semibold">Premio disponibile!</span>
              }
            </p>
          </div>

          {/* Reward alert */}
          {cardData.rewards_available > 0 && (
            <div className="bg-[#238636]/20 border border-[#238636] rounded-xl p-4 text-center">
              <Star size={24} className="mx-auto text-[#238636] mb-1" />
              <p className="text-[#238636] font-bold">
                {cardData.rewards_available} Premio{cardData.rewards_available > 1 ? 'i' : ''} Disponibil{cardData.rewards_available > 1 ? 'i' : 'e'}!
              </p>
              <p className="text-[#8B949E] text-xs">Presentala in negozio per lo sconto</p>
            </div>
          )}

          <div className="h-px bg-[#30363D]" />

          {/* Info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#0D1117] rounded-lg p-3 text-center">
              <p className="text-[#8B949E] text-xs">Ordini</p>
              <p className="text-xl font-bold text-white">{cardData.total_orders}</p>
            </div>
            <div className="bg-[#0D1117] rounded-lg p-3 text-center">
              <p className="text-[#8B949E] text-xs">Prossimo premio</p>
              <p className="text-xl font-bold text-[#F0883E]">-{cardData.points_to_next_reward}</p>
            </div>
          </div>

          {/* QR Code area */}
          <div className="bg-white rounded-xl p-4 flex flex-col items-center">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${cardData.company_id || 'SCG'}|${cardData.card_number}&bgcolor=ffffff&color=000000`}
              alt="QR Code"
              className="w-48 h-48"
            />
            <p className="text-gray-500 text-xs mt-2">Scansiona in negozio</p>
          </div>

          {/* Rules */}
          <div className="text-center space-y-1">
            <p className="text-[#6E7681] text-xs">1 punto per ogni euro speso</p>
            <p className="text-[#F0883E] text-xs font-medium">{cardData.reward_threshold} punti = {cardData.reward_discount}% di sconto</p>
          </div>
        </div>

        {/* Bottom accent */}
        <div className="h-1.5 bg-[#58A6FF] rounded-b-2xl mt-0" />
      </div>

      {/* Add to Home Screen */}
      {!isStandalone && (
        <div className="w-full max-w-sm mt-6 bg-[#161B22] border border-[#30363D] rounded-xl p-4 text-center">
          {deferredPrompt ? (
            <>
              <button
                onClick={handleInstall}
                className="w-full flex items-center justify-center gap-2 bg-[#58A6FF] text-white py-3 rounded-xl font-semibold hover:bg-[#79B8FF] transition-colors mb-2"
              >
                <Download size={20} />
                Aggiungi alla Home
              </button>
              <p className="text-[#8B949E] text-xs">Avrai la tessera sempre a portata di mano</p>
            </>
          ) : (
            <>
              <p className="text-white font-semibold text-sm mb-2">Salva la Tessera</p>
              <p className="text-[#8B949E] text-xs leading-relaxed">
                <span className="text-[#58A6FF]">iPhone:</span> Tocca <span className="text-white">⬆</span> poi "Aggiungi a Home"<br />
                <span className="text-[#58A6FF]">Android:</span> Tocca <span className="text-white">⋮</span> poi "Aggiungi a schermata Home"
              </p>
            </>
          )}
        </div>
      )}

      <p className="text-[#30363D] text-xs mt-6 mb-4">{cardData.shop_name} • Tessera Fedeltà</p>
    </div>
  );
};

// ============ PIN LOCK SCREEN ============

const PinLockScreen = ({ onUnlock, onSuperAdmin }) => {
  const [step, setStep] = useState("company"); // "company" | "pin" | "super-pin"
  const [companyCode, setCompanyCode] = useState("");
  const [companyInfo, setCompanyInfo] = useState(null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const loginInProgress = useRef(false);

  const verifyCompany = async () => {
    if (!companyCode.trim()) return;
    setLoading(true);
    setError(false);
    try {
      const response = await axios.get(`${API}/auth/company/${companyCode.trim()}`);
      setCompanyInfo(response.data);
      setStep("pin");
    } catch {
      setError(true);
      setErrorMsg("Codice azienda non trovato");
    } finally {
      setLoading(false);
    }
  };

  const tryLogin = async (pinValue) => {
    if (loginInProgress.current) return;
    loginInProgress.current = true;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        company_code: companyCode.trim(),
        pin: pinValue
      });
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(response.data));
      onUnlock(response.data);
    } catch {
      setError(true);
      setErrorMsg("PIN errato");
      setTimeout(() => { setPin(""); setError(false); setErrorMsg(""); }, 1000);
    } finally {
      setLoading(false);
      loginInProgress.current = false;
    }
  };

  const trySuperLogin = async (pinValue) => {
    if (loginInProgress.current) return;
    loginInProgress.current = true;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/super-login`, { pin: pinValue });
      const data = { ...response.data, role: "super_admin" };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
      onSuperAdmin(data);
    } catch {
      setError(true);
      setErrorMsg("PIN Super Admin errato");
      setTimeout(() => { setPin(""); setError(false); setErrorMsg(""); }, 1000);
    } finally {
      setLoading(false);
      loginInProgress.current = false;
    }
  };

  const handleDigit = useCallback((digit) => {
    if (loading || loginInProgress.current) return;
    setPin(prev => {
      if (prev.length >= 6) return prev;
      const newPin = prev + digit;
      setError(false);
      setErrorMsg("");
      if (newPin.length === 6) {
        setTimeout(() => {
          if (step === "super-pin") trySuperLogin(newPin);
          else tryLogin(newPin);
        }, 0);
      }
      return newPin;
    });
  }, [loading, step, companyCode]);

  const handleDelete = useCallback(() => {
    setPin(prev => prev.slice(0, -1));
  }, []);

  const handleBack = () => {
    setStep("company");
    setPin("");
    setError(false);
    setErrorMsg("");
    setCompanyInfo(null);
  };

  const handleSuperAdmin = () => {
    setStep("super-pin");
    setPin("");
    setError(false);
    setErrorMsg("");
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (step === "company") {
        if (e.key === "Enter") verifyCompany();
        return;
      }
      if (e.key >= '0' && e.key <= '9') handleDigit(e.key);
      else if (e.key === 'Backspace' || e.key === 'Delete') handleDelete();
      else if (e.key === 'Escape') handleBack();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleDigit, handleDelete, step, companyCode]);

  return (
    <div className="min-h-screen bg-[#0D1117] flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-xs text-center">
        {/* Platform branding */}
        <div className="mb-8">
          <img src="/logo-scd.png" alt="S.C. Digital" className="w-20 h-20 mx-auto mb-4 object-contain" />
          <h1 data-testid="platform-title" className="text-2xl font-bold text-white">S.C. Digital</h1>
          {step === "company" && <p className="text-[#8B949E] text-sm mt-1">Inserisci il codice azienda</p>}
          {step === "pin" && companyInfo && (
            <div className="mt-2">
              {companyInfo.logo && (
                <img src={`${BACKEND_URL}${companyInfo.logo}`} alt="" className="w-12 h-12 mx-auto mb-2 rounded-xl object-contain" />
              )}
              <p className="text-[#58A6FF] font-semibold">{companyInfo.name}</p>
              <p className="text-[#8B949E] text-sm">Inserisci il PIN</p>
            </div>
          )}
          {step === "super-pin" && (
            <div className="mt-2">
              <div className="flex items-center justify-center gap-1.5 text-[#A371F7]">
                <Shield size={16} />
                <span className="font-semibold">Super Admin</span>
              </div>
              <p className="text-[#8B949E] text-sm mt-1">Inserisci il PIN</p>
            </div>
          )}
        </div>

        {/* Step: Company Code */}
        {step === "company" && (
          <div className="space-y-4">
            <input
              data-testid="company-code-input"
              type="text"
              value={companyCode}
              onChange={e => { setCompanyCode(e.target.value.toUpperCase()); setError(false); }}
              placeholder="Es. SCG"
              className="w-full px-4 py-4 bg-[#161B22] border border-[#30363D] rounded-xl text-white text-center text-2xl font-bold tracking-widest placeholder:text-[#30363D] placeholder:text-lg placeholder:font-normal focus:outline-none focus:border-[#58A6FF] transition-colors uppercase"
              autoFocus
              maxLength={10}
            />
            {error && <p data-testid="company-error" className="text-red-400 text-sm animate-fadeIn">{errorMsg}</p>}
            <button
              data-testid="verify-company-btn"
              onClick={verifyCompany}
              disabled={loading || !companyCode.trim()}
              className="w-full py-4 bg-[#58A6FF] text-white rounded-xl font-semibold hover:bg-[#79B8FF] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 size={20} className="animate-spin" /> : <ChevronRight size={20} />}
              Continua
            </button>
            <button
              data-testid="super-admin-btn"
              onClick={handleSuperAdmin}
              className="w-full py-3 text-[#A371F7] text-sm hover:bg-[#A371F7]/10 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <Shield size={16} />
              Accesso Super Admin
            </button>
          </div>
        )}

        {/* Step: PIN entry (company or super-admin) */}
        {(step === "pin" || step === "super-pin") && (
          <>
            {/* Back button */}
            <button
              data-testid="back-to-company-btn"
              onClick={handleBack}
              className="mb-6 text-[#8B949E] text-sm hover:text-white transition-colors flex items-center justify-center gap-1.5 mx-auto"
            >
              <ArrowLeft size={16} />
              Cambia azienda
            </button>

            {/* PIN dots */}
            <div className="flex justify-center gap-3 mb-8">
              {[0,1,2,3,4,5].map(i => (
                <div
                  key={i}
                  className={`w-4 h-4 rounded-full transition-all ${
                    error ? 'bg-red-500' :
                    i < pin.length ? (step === "super-pin" ? 'bg-[#A371F7] scale-110' : 'bg-[#58A6FF] scale-110') : 'bg-[#30363D]'
                  }`}
                />
              ))}
            </div>

            {loading && <Loader2 size={24} className={`${step === "super-pin" ? "text-[#A371F7]" : "text-[#58A6FF]"} animate-spin mx-auto mb-4`} />}
            {error && <p data-testid="pin-error" className="text-red-400 text-sm mb-4 animate-fadeIn">{errorMsg}</p>}

            {/* Numpad */}
            <div className="grid grid-cols-3 gap-3 max-w-[240px] mx-auto">
              {[1,2,3,4,5,6,7,8,9].map(num => (
                <button
                  key={num}
                  data-testid={`pin-digit-${num}`}
                  onClick={() => handleDigit(String(num))}
                  className={`w-16 h-16 rounded-2xl bg-[#161B22] border border-[#30363D] text-white text-2xl font-semibold hover:bg-[#21262D] transition-colors ${step === "super-pin" ? "active:bg-[#A371F7]/20" : "active:bg-[#58A6FF]/20"}`}
                >
                  {num}
                </button>
              ))}
              <div />
              <button
                data-testid="pin-digit-0"
                onClick={() => handleDigit("0")}
                className={`w-16 h-16 rounded-2xl bg-[#161B22] border border-[#30363D] text-white text-2xl font-semibold hover:bg-[#21262D] transition-colors ${step === "super-pin" ? "active:bg-[#A371F7]/20" : "active:bg-[#58A6FF]/20"}`}
              >
                0
              </button>
              <button
                data-testid="pin-delete-btn"
                onClick={handleDelete}
                className="w-16 h-16 rounded-2xl bg-[#161B22] border border-[#30363D] text-[#8B949E] text-lg hover:bg-[#21262D] transition-colors flex items-center justify-center"
              >
                <ArrowLeft size={20} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============ SETTINGS PAGE ============

const SettingsPage = ({ onNavigate, currentAccount }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [loyaltySettings, setLoyaltySettings] = useState(null);
  const [loyaltyEditing, setLoyaltyEditing] = useState(false);
  const [loyaltyForm, setLoyaltyForm] = useState({ reward_discount: 10, reward_threshold: 100, points_per_euro: 1 });
  const [loyaltySaving, setLoyaltySaving] = useState(false);

  useEffect(() => { fetchAccounts(); fetchLoyalty(); }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`);
      setAccounts(response.data);
    } catch { toast.error("Errore caricamento account"); }
    finally { setLoading(false); }
  };

  const fetchLoyalty = async () => {
    try {
      const response = await axios.get(`${API}/loyalty-settings`);
      setLoyaltySettings(response.data);
      setLoyaltyForm(response.data);
    } catch {}
  };

  const handleSaveLoyalty = async () => {
    setLoyaltySaving(true);
    try {
      await axios.put(`${API}/loyalty-settings`, loyaltyForm);
      toast.success("Impostazioni fedeltà salvate!");
      setLoyaltySettings({ ...loyaltyForm });
      setLoyaltyEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally { setLoyaltySaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo account?")) return;
    try {
      await axios.delete(`${API}/accounts/${id}`);
      toast.success("Account eliminato");
      fetchAccounts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="app-container min-h-screen flex items-center justify-center">
        <Loader2 size={48} className="text-[#58A6FF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="app-container min-h-screen">
      <header className="app-header">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings size={24} className="text-[#8B949E]" />
            <h1 className="text-xl font-bold text-white">Impostazioni</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => onNavigate('dashboard')} className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-2.5 py-1.5 rounded-lg hover:bg-[#30363D] transition-colors text-sm">
              <BarChart3 size={14} /> Dashboard
            </button>
            <button onClick={handleLogout} className="flex items-center gap-1.5 bg-red-500/20 text-red-400 px-2.5 py-1.5 rounded-lg hover:bg-red-500/30 transition-colors text-sm">
              <ArrowLeft size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Current user */}
        <div className="bg-[#161B22] border border-[#58A6FF] rounded-xl p-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#58A6FF]/20 flex items-center justify-center">
            <User size={24} className="text-[#58A6FF]" />
          </div>
          <div>
            <p className="text-white font-semibold">{currentAccount?.name}</p>
            <p className="text-[#58A6FF] text-sm">{currentAccount?.role === 'admin' ? 'Amministratore' : 'Dipendente'}</p>
          </div>
        </div>

        {/* Loyalty Settings */}
        {loyaltySettings && (
          <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Gift size={20} className="text-[#F0883E]" />
                Programma Fedeltà
              </h2>
              {!loyaltyEditing ? (
                <button
                  data-testid="edit-loyalty-btn"
                  onClick={() => { setLoyaltyForm({ ...loyaltySettings }); setLoyaltyEditing(true); }}
                  className="flex items-center gap-1.5 bg-[#21262D] text-[#C9D1D9] px-3 py-2 rounded-lg hover:bg-[#30363D] transition-colors text-sm"
                >
                  <Edit size={14} /> Modifica
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => setLoyaltyEditing(false)}
                    className="px-3 py-2 bg-[#21262D] text-[#C9D1D9] rounded-lg hover:bg-[#30363D] text-sm"
                  >
                    Annulla
                  </button>
                  <button
                    data-testid="save-loyalty-btn"
                    onClick={handleSaveLoyalty}
                    disabled={loyaltySaving}
                    className="flex items-center gap-1.5 bg-[#238636] text-white px-3 py-2 rounded-lg hover:bg-[#2ea043] text-sm disabled:opacity-50"
                  >
                    {loyaltySaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Salva
                  </button>
                </div>
              )}
            </div>

            {!loyaltyEditing ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-[#0D1117] rounded-xl p-4 text-center">
                  <p className="text-[#8B949E] text-xs mb-1">Sconto Premio</p>
                  <p className="text-3xl font-bold text-[#F0883E]">{loyaltySettings.reward_discount}%</p>
                </div>
                <div className="bg-[#0D1117] rounded-xl p-4 text-center">
                  <p className="text-[#8B949E] text-xs mb-1">Soglia Punti</p>
                  <p className="text-3xl font-bold text-[#58A6FF]">{loyaltySettings.reward_threshold}</p>
                  <p className="text-[#6E7681] text-xs">punti per premio</p>
                </div>
                <div className="bg-[#0D1117] rounded-xl p-4 text-center">
                  <p className="text-[#8B949E] text-xs mb-1">Punti per Euro</p>
                  <p className="text-3xl font-bold text-[#238636]">{loyaltySettings.points_per_euro}</p>
                  <p className="text-[#6E7681] text-xs">punto ogni euro speso</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="field-label">Sconto Premio (%)</label>
                  <input
                    data-testid="loyalty-discount-input"
                    type="number" min="1" max="100"
                    value={loyaltyForm.reward_discount}
                    onChange={e => setLoyaltyForm({ ...loyaltyForm, reward_discount: parseInt(e.target.value) || 0 })}
                    className="field-input text-center text-lg font-bold"
                  />
                  <p className="text-[#6E7681] text-xs mt-1">% di sconto applicato come premio</p>
                </div>
                <div>
                  <label className="field-label">Soglia Punti</label>
                  <input
                    data-testid="loyalty-threshold-input"
                    type="number" min="1"
                    value={loyaltyForm.reward_threshold}
                    onChange={e => setLoyaltyForm({ ...loyaltyForm, reward_threshold: parseInt(e.target.value) || 0 })}
                    className="field-input text-center text-lg font-bold"
                  />
                  <p className="text-[#6E7681] text-xs mt-1">Punti necessari per un premio</p>
                </div>
                <div>
                  <label className="field-label">Punti per Euro</label>
                  <input
                    data-testid="loyalty-ppe-input"
                    type="number" min="1"
                    value={loyaltyForm.points_per_euro}
                    onChange={e => setLoyaltyForm({ ...loyaltyForm, points_per_euro: parseInt(e.target.value) || 0 })}
                    className="field-input text-center text-lg font-bold"
                  />
                  <p className="text-[#6E7681] text-xs mt-1">Punti guadagnati per ogni euro</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Accounts List */}
        <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users size={20} className="text-[#A371F7]" />
              Account
            </h2>
            <button
              data-testid="add-account-btn"
              onClick={() => { setEditingAccount(null); setShowNewModal(true); }}
              className="flex items-center gap-1.5 bg-[#238636] text-white px-3 py-2 rounded-lg hover:bg-[#2ea043] transition-colors text-sm"
            >
              <Plus size={16} /> Nuovo Account
            </button>
          </div>

          <div className="space-y-3">
            {accounts.map(acc => (
              <div key={acc.id} className="bg-[#0D1117] border border-[#30363D] rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${acc.role === 'admin' ? 'bg-[#58A6FF]/20' : 'bg-[#F0883E]/20'}`}>
                      <User size={20} className={acc.role === 'admin' ? 'text-[#58A6FF]' : 'text-[#F0883E]'} />
                    </div>
                    <div>
                      <p className="text-white font-semibold">{acc.name}</p>
                      <p className={`text-xs ${acc.role === 'admin' ? 'text-[#58A6FF]' : 'text-[#F0883E]'}`}>
                        {acc.role === 'admin' ? 'Amministratore' : 'Dipendente'}
                      </p>
                    </div>
                  </div>
                  {acc.role !== 'admin' && (
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingAccount(acc); setShowNewModal(true); }} className="p-1.5 text-[#58A6FF] hover:bg-[#21262D] rounded-lg"><Edit size={16} /></button>
                      <button onClick={() => handleDelete(acc.id)} className="p-1.5 text-red-400 hover:bg-[#21262D] rounded-lg"><Trash2 size={16} /></button>
                    </div>
                  )}
                </div>
                {/* Permissions */}
                <div className="flex flex-wrap gap-2">
                  {Object.entries(acc.permissions || {}).map(([key, val]) => (
                    <span key={key} className={`px-2 py-1 rounded-full text-xs ${val ? 'bg-[#238636]/20 text-[#238636]' : 'bg-[#30363D] text-[#6E7681]'}`}>
                      {key === 'dashboard' ? 'Dashboard' : key === 'orders' ? 'Ordini' : key === 'customers' ? 'Clienti' : key === 'inventory' ? 'Magazzino' : key === 'scanner' ? 'Scanner' : key === 'export' ? 'Export' : key === 'settings' ? 'Impostazioni' : key}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Account Modal */}
      {showNewModal && (
        <AccountModal
          account={editingAccount}
          onClose={() => setShowNewModal(false)}
          onSaved={() => { setShowNewModal(false); fetchAccounts(); }}
        />
      )}
    </div>
  );
};

// Account Create/Edit Modal
const AccountModal = ({ account, onClose, onSaved }) => {
  const [name, setName] = useState(account?.name || "");
  const [pin, setPin] = useState("");
  const [perms, setPerms] = useState(account?.permissions || {
    dashboard: true, orders: true, customers: false, inventory: false, scanner: true, export: false, settings: false
  });
  const [saving, setSaving] = useState(false);

  const permLabels = {
    dashboard: 'Dashboard', orders: 'Ordini', customers: 'Clienti',
    inventory: 'Magazzino', scanner: 'Scanner', export: 'Export'
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Inserisci un nome"); return; }
    if (!account && (!pin || pin.length < 4)) { toast.error("PIN di almeno 4 cifre"); return; }
    setSaving(true);
    try {
      if (account) {
        const data = { name, permissions: perms };
        if (pin) data.pin = pin;
        await axios.put(`${API}/accounts/${account.id}`, data);
        toast.success("Account aggiornato");
      } else {
        await axios.post(`${API}/accounts`, { name, pin, role: "employee", permissions: perms });
        toast.success("Account creato");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#161B22] w-full max-w-md rounded-2xl animate-fadeIn" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-5 border-b border-[#30363D]">
          <h3 className="text-xl font-bold text-white">{account ? 'Modifica Account' : 'Nuovo Dipendente'}</h3>
          <button onClick={onClose} className="text-[#8B949E] hover:text-white"><X size={24} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="field-label">Nome</label>
            <input data-testid="account-name-input" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Nome dipendente" className="field-input" />
          </div>
          <div>
            <label className="field-label">{account ? 'Nuovo PIN (lascia vuoto per non cambiare)' : 'PIN di accesso'}</label>
            <input data-testid="account-pin-input" type="password" inputMode="numeric" value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder={account ? '••••••' : 'Minimo 4 cifre'} className="field-input" />
          </div>
          <div>
            <label className="field-label mb-3 block">Permessi — Cosa può vedere</label>
            <div className="space-y-2">
              {Object.entries(permLabels).map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 p-3 bg-[#0D1117] rounded-lg cursor-pointer hover:bg-[#21262D] transition-colors">
                  <input
                    type="checkbox"
                    checked={perms[key] || false}
                    onChange={e => setPerms({ ...perms, [key]: e.target.checked })}
                    className="w-5 h-5 rounded accent-[#58A6FF]"
                  />
                  <span className="text-[#C9D1D9] text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-[#30363D] flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-3 bg-[#21262D] text-[#C9D1D9] rounded-xl hover:bg-[#30363D]">Annulla</button>
          <button data-testid="save-account-btn" onClick={handleSave} disabled={saving} className="flex-1 px-4 py-3 bg-[#238636] text-white rounded-xl hover:bg-[#2ea043] flex items-center justify-center gap-2">
            {saving ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
            Salva
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ SUPER ADMIN DASHBOARD ============

const SuperAdminDashboard = ({ onLogout }) => {
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [resetPinCompany, setResetPinCompany] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const auth = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      const [companiesRes, statsRes] = await Promise.all([
        axios.get(`${API}/super-admin/companies`, { headers }),
        axios.get(`${API}/super-admin/stats`, { headers })
      ]);
      setCompanies(companiesRes.data);
      setStats(statsRes.data);
    } catch { toast.error("Errore caricamento dati"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (code, name) => {
    if (!window.confirm(`Eliminare l'azienda "${name}" e TUTTI i suoi dati? Questa azione è irreversibile!`)) return;
    try {
      const auth = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      await axios.delete(`${API}/super-admin/companies/${code}`, { headers });
      toast.success(`Azienda "${name}" eliminata`);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    onLogout();
  };

  if (loading) {
    return (
      <div className="app-container min-h-screen flex items-center justify-center">
        <Loader2 size={48} className="text-[#A371F7] animate-spin" />
      </div>
    );
  }

  return (
    <div className="app-container min-h-screen">
      <header className="app-header">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-scd.png" alt="S.C. Digital" className="w-10 h-10 object-contain" />
            <div>
              <h1 data-testid="super-admin-title" className="text-xl font-bold text-white">S.C. Digital</h1>
              <p className="text-[#A371F7] text-xs">Pannello Super Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="change-pin-btn"
              onClick={() => setShowPinModal(true)}
              className="flex items-center gap-1.5 bg-[#A371F7]/20 text-[#A371F7] px-3 py-2 rounded-lg hover:bg-[#A371F7]/30 transition-colors text-sm"
            >
              <Lock size={16} /> Cambia PIN
            </button>
            <button
              data-testid="super-admin-logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-1.5 bg-red-500/20 text-red-400 px-3 py-2 rounded-lg hover:bg-red-500/30 transition-colors text-sm"
            >
              <LogOut size={16} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Global Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Aziende", value: stats.companies, icon: Building2, color: "#A371F7" },
              { label: "Ordini Totali", value: stats.total_orders, icon: Package, color: "#58A6FF" },
              { label: "Clienti Totali", value: stats.total_customers, icon: Users, color: "#238636" },
              { label: "Account Totali", value: stats.total_accounts, icon: User, color: "#F0883E" },
            ].map(s => (
              <div key={s.label} className="bg-[#161B22] border border-[#30363D] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <s.icon size={18} style={{ color: s.color }} />
                  <span className="text-[#8B949E] text-xs">{s.label}</span>
                </div>
                <p className="text-2xl font-bold text-white">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Companies list */}
        <div className="bg-[#161B22] border border-[#30363D] rounded-xl p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Building2 size={20} className="text-[#A371F7]" />
              Aziende Registrate
            </h2>
            <button
              data-testid="create-company-btn"
              onClick={() => { setEditingCompany(null); setShowCreateModal(true); }}
              className="flex items-center gap-1.5 bg-[#A371F7] text-white px-4 py-2 rounded-lg hover:bg-[#8B5CF6] transition-colors text-sm font-medium"
            >
              <Plus size={16} /> Nuova Azienda
            </button>
          </div>

          {companies.length === 0 ? (
            <div className="text-center py-12 text-[#8B949E]">
              <Building2 size={48} className="mx-auto mb-3 opacity-30" />
              <p>Nessuna azienda registrata</p>
            </div>
          ) : (
            <div className="space-y-3">
              {companies.map(company => (
                <div key={company.code} className="bg-[#0D1117] border border-[#30363D] rounded-xl p-4 hover:border-[#A371F7]/30 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 font-bold text-white text-lg overflow-hidden"
                        style={{ backgroundColor: company.card_color + "30", color: company.card_color }}
                      >
                        {company.logo ? (
                          <img src={`${BACKEND_URL}${company.logo}`} alt="" className="w-full h-full object-contain" />
                        ) : (
                          company.code.slice(0, 2)
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white font-semibold">{company.name}</p>
                          <span className="text-[#A371F7] bg-[#A371F7]/10 px-2 py-0.5 rounded text-xs font-mono">{company.code}</span>
                        </div>
                        {company.tagline && <p className="text-[#8B949E] text-sm truncate">{company.tagline}</p>}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button
                        data-testid={`reset-pin-${company.code}`}
                        onClick={() => setResetPinCompany(company)}
                        className="p-2 text-[#E3B341] hover:bg-[#21262D] rounded-lg transition-colors"
                        title="Cambia PIN Admin"
                      >
                        <KeyRound size={16} />
                      </button>
                      <button
                        data-testid={`edit-company-${company.code}`}
                        onClick={() => { setEditingCompany(company); setShowCreateModal(true); }}
                        className="p-2 text-[#58A6FF] hover:bg-[#21262D] rounded-lg transition-colors"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        data-testid={`delete-company-${company.code}`}
                        onClick={() => handleDelete(company.code, company.name)}
                        className="p-2 text-red-400 hover:bg-[#21262D] rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  {/* Stats row */}
                  <div className="flex gap-4 mt-3 pt-3 border-t border-[#21262D]">
                    <div className="flex items-center gap-1.5">
                      <Package size={14} className="text-[#58A6FF]" />
                      <span className="text-[#C9D1D9] text-sm">{company.stats?.orders || 0} ordini</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Users size={14} className="text-[#238636]" />
                      <span className="text-[#C9D1D9] text-sm">{company.stats?.customers || 0} clienti</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <User size={14} className="text-[#F0883E]" />
                      <span className="text-[#C9D1D9] text-sm">{company.stats?.accounts || 0} account</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Euro size={14} className="text-[#E3B341]" />
                      <span className="text-[#C9D1D9] text-sm">{(company.stats?.revenue || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {showCreateModal && (
        <CompanyModal
          company={editingCompany}
          onClose={() => setShowCreateModal(false)}
          onSaved={() => { setShowCreateModal(false); fetchData(); }}
        />
      )}
      {showPinModal && (
        <ChangePinModal onClose={() => setShowPinModal(false)} />
      )}
      {resetPinCompany && (
        <ResetAdminPinModal
          company={resetPinCompany}
          onClose={() => setResetPinCompany(null)}
        />
      )}
    </div>
  );
};

// Change Super Admin PIN Modal
const ChangePinModal = ({ onClose }) => {
  const [oldPin, setOldPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!oldPin || oldPin.length < 4) { toast.error("Inserisci il PIN attuale"); return; }
    if (!newPin || newPin.length < 4) { toast.error("Nuovo PIN: almeno 4 cifre"); return; }
    if (newPin !== confirmPin) { toast.error("I due PIN non corrispondono"); return; }
    if (oldPin === newPin) { toast.error("Il nuovo PIN deve essere diverso dal vecchio"); return; }
    setSaving(true);
    try {
      const auth = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      await axios.put(`${API}/super-admin/pin`, { old_pin: oldPin, new_pin: newPin }, { headers });
      toast.success("PIN Super Admin aggiornato!");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore aggiornamento PIN");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#161B22] w-full max-w-sm rounded-2xl animate-fadeIn" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-5 border-b border-[#30363D]">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <Lock size={20} className="text-[#A371F7]" />
            Cambia PIN Super Admin
          </h3>
          <button onClick={onClose} className="text-[#8B949E] hover:text-white"><X size={24} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="field-label">PIN Attuale</label>
            <input
              data-testid="old-pin-input"
              type="password" inputMode="numeric"
              value={oldPin}
              onChange={e => setOldPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="PIN corrente"
              className="field-input"
              autoFocus
            />
          </div>
          <div>
            <label className="field-label">Nuovo PIN</label>
            <input
              data-testid="new-pin-input"
              type="password" inputMode="numeric"
              value={newPin}
              onChange={e => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Min. 4 cifre"
              className="field-input"
            />
          </div>
          <div>
            <label className="field-label">Conferma Nuovo PIN</label>
            <input
              data-testid="confirm-pin-input"
              type="password" inputMode="numeric"
              value={confirmPin}
              onChange={e => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Ripeti il nuovo PIN"
              className="field-input"
            />
          </div>
        </div>
        <div className="p-5 border-t border-[#30363D] flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-3 bg-[#21262D] text-[#C9D1D9] rounded-xl hover:bg-[#30363D]">Annulla</button>
          <button
            data-testid="save-pin-btn"
            onClick={handleSave} disabled={saving}
            className="flex-1 px-4 py-3 bg-[#A371F7] text-white rounded-xl hover:bg-[#8B5CF6] flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
            Salva PIN
          </button>
        </div>
      </div>
    </div>
  );
};

// Reset Company Admin PIN Modal
const ResetAdminPinModal = ({ company, onClose }) => {
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!newPin || newPin.length < 4) { toast.error("Nuovo PIN: almeno 4 cifre"); return; }
    if (newPin !== confirmPin) { toast.error("I due PIN non corrispondono"); return; }
    setSaving(true);
    try {
      const auth = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      await axios.put(`${API}/super-admin/companies/${company.code}/admin-pin`, { new_pin: newPin }, { headers });
      toast.success(`PIN admin di "${company.name}" aggiornato!`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore aggiornamento PIN");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#161B22] w-full max-w-sm rounded-2xl animate-fadeIn" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-5 border-b border-[#30363D]">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <KeyRound size={20} className="text-[#E3B341]" />
            Cambia PIN Admin
          </h3>
          <button onClick={onClose} className="text-[#8B949E] hover:text-white"><X size={24} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3 bg-[#0D1117] border border-[#30363D] rounded-xl p-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 font-bold text-sm overflow-hidden"
              style={{ backgroundColor: company.card_color + "30", color: company.card_color }}
            >
              {company.logo ? (
                <img src={`${BACKEND_URL}${company.logo}`} alt="" className="w-full h-full object-contain" />
              ) : company.code.slice(0, 2)}
            </div>
            <div>
              <p className="text-white font-semibold text-sm">{company.name}</p>
              <p className="text-[#8B949E] text-xs font-mono">{company.code}</p>
            </div>
          </div>
          <div>
            <label className="field-label">Nuovo PIN Admin</label>
            <input
              data-testid="admin-new-pin-input"
              type="password" inputMode="numeric"
              value={newPin}
              onChange={e => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Min. 4 cifre"
              className="field-input"
              autoFocus
            />
          </div>
          <div>
            <label className="field-label">Conferma Nuovo PIN</label>
            <input
              data-testid="admin-confirm-pin-input"
              type="password" inputMode="numeric"
              value={confirmPin}
              onChange={e => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Ripeti il nuovo PIN"
              className="field-input"
            />
          </div>
        </div>
        <div className="p-5 border-t border-[#30363D] flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-3 bg-[#21262D] text-[#C9D1D9] rounded-xl hover:bg-[#30363D]">Annulla</button>
          <button
            data-testid="save-admin-pin-btn"
            onClick={handleSave} disabled={saving}
            className="flex-1 px-4 py-3 bg-[#E3B341] text-[#0D1117] rounded-xl hover:bg-[#F0C85A] flex items-center justify-center gap-2 font-semibold disabled:opacity-50"
          >
            {saving ? <Loader2 size={20} className="animate-spin" /> : <KeyRound size={20} />}
            Salva PIN
          </button>
        </div>
      </div>
    </div>
  );
};

// Company Create/Edit Modal
const CompanyModal = ({ company, onClose, onSaved }) => {
  const isEdit = !!company;
  const [code, setCode] = useState(company?.code || "");
  const [name, setName] = useState(company?.name || "");
  const [tagline, setTagline] = useState(company?.tagline || "");
  const [cardColor, setCardColor] = useState(company?.card_color || "#58A6FF");
  const [adminName, setAdminName] = useState("");
  const [adminPin, setAdminPin] = useState("");
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(company?.logo ? `${BACKEND_URL}${company.logo}` : null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleziona un'immagine"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Immagine troppo grande (max 2MB)"); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async (companyCode) => {
    if (!logoFile) return;
    setUploadingLogo(true);
    try {
      const auth = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
      const headers = { "Content-Type": "multipart/form-data" };
      if (auth.token) headers["Authorization"] = `Bearer ${auth.token}`;
      const formData = new FormData();
      formData.append("file", logoFile);
      await axios.post(`${API}/super-admin/companies/${companyCode}/logo`, formData, { headers });
    } catch (err) {
      toast.error("Errore upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Inserisci il nome azienda"); return; }
    if (!isEdit) {
      if (!code.trim() || code.trim().length < 2) { toast.error("Codice azienda: minimo 2 caratteri"); return; }
      if (!adminName.trim()) { toast.error("Inserisci il nome dell'amministratore"); return; }
      if (!adminPin || adminPin.length < 4) { toast.error("PIN admin: almeno 4 cifre"); return; }
    }
    setSaving(true);
    try {
      const auth = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
      const headers = auth.token ? { Authorization: `Bearer ${auth.token}` } : {};
      let targetCode = isEdit ? company.code : code.trim().toUpperCase();
      if (isEdit) {
        await axios.put(`${API}/super-admin/companies/${company.code}`, { name, tagline, card_color: cardColor }, { headers });
      } else {
        await axios.post(`${API}/super-admin/companies`, {
          code: targetCode,
          name: name.trim(),
          tagline: tagline.trim(),
          admin_name: adminName.trim(),
          admin_pin: adminPin,
          card_color: cardColor
        }, { headers });
      }
      if (logoFile) await uploadLogo(targetCode);
      toast.success(isEdit ? "Azienda aggiornata" : "Azienda creata con successo!");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const colorPresets = ["#58A6FF", "#A371F7", "#238636", "#F0883E", "#E3B341", "#F778BA", "#FF7B72", "#79C0FF"];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#161B22] w-full max-w-md rounded-2xl animate-fadeIn max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-5 border-b border-[#30363D]">
          <h3 className="text-xl font-bold text-white">{isEdit ? 'Modifica Azienda' : 'Nuova Azienda'}</h3>
          <button onClick={onClose} className="text-[#8B949E] hover:text-white"><X size={24} /></button>
        </div>
        <div className="p-5 space-y-4">
          {!isEdit && (
            <div>
              <label className="field-label">Codice Azienda (univoco, 2-10 caratteri)</label>
              <input
                data-testid="company-code-create-input"
                type="text" value={code}
                onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))}
                placeholder="Es. SCG, ACME"
                className="field-input font-mono uppercase tracking-widest"
              />
            </div>
          )}
          <div>
            <label className="field-label">Nome Azienda</label>
            <input data-testid="company-name-input" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Nome completo" className="field-input" />
          </div>
          <div>
            <label className="field-label">Tagline (opzionale)</label>
            <input type="text" value={tagline} onChange={e => setTagline(e.target.value)} placeholder="Slogan o descrizione breve" className="field-input" />
          </div>
          {/* Logo upload */}
          <div>
            <label className="field-label flex items-center gap-2">Logo Azienda</label>
            <div className="flex items-center gap-4">
              {logoPreview ? (
                <div className="relative">
                  <img src={logoPreview} alt="Logo" className="w-16 h-16 rounded-xl object-contain bg-[#0D1117] border border-[#30363D]" />
                  <button
                    onClick={() => { setLogoFile(null); setLogoPreview(null); }}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                  >
                    <X size={12} className="text-white" />
                  </button>
                </div>
              ) : (
                <div className="w-16 h-16 rounded-xl bg-[#0D1117] border border-[#30363D] border-dashed flex items-center justify-center">
                  <Building2 size={24} className="text-[#30363D]" />
                </div>
              )}
              <label className="cursor-pointer flex-1">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-[#21262D] text-[#C9D1D9] rounded-lg hover:bg-[#30363D] transition-colors text-sm">
                  <Plus size={16} />
                  {logoPreview ? 'Cambia logo' : 'Carica logo'}
                </div>
                <input
                  data-testid="company-logo-input"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="hidden"
                />
              </label>
            </div>
            <p className="text-[#6E7681] text-xs mt-1.5">JPG, PNG, WebP - Max 2MB</p>
          </div>
          <div>
            <label className="field-label flex items-center gap-2"><Palette size={14} /> Colore Tema</label>
            <div className="flex gap-2 flex-wrap mb-2">
              {colorPresets.map(c => (
                <button
                  key={c}
                  onClick={() => setCardColor(c)}
                  className={`w-8 h-8 rounded-lg border-2 transition-all ${cardColor === c ? 'border-white scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <input type="color" value={cardColor} onChange={e => setCardColor(e.target.value)} className="w-full h-10 rounded-lg cursor-pointer bg-transparent" />
          </div>
          {!isEdit && (
            <>
              <div className="h-px bg-[#30363D]" />
              <p className="text-[#8B949E] text-sm">Account Amministratore</p>
              <div>
                <label className="field-label">Nome Admin</label>
                <input data-testid="company-admin-name-input" type="text" value={adminName} onChange={e => setAdminName(e.target.value)} placeholder="Nome amministratore" className="field-input" />
              </div>
              <div>
                <label className="field-label">PIN Admin (min. 4 cifre)</label>
                <input
                  data-testid="company-admin-pin-input"
                  type="password" inputMode="numeric" value={adminPin}
                  onChange={e => setAdminPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Es. 123456"
                  className="field-input"
                />
              </div>
            </>
          )}
        </div>
        <div className="p-5 border-t border-[#30363D] flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-3 bg-[#21262D] text-[#C9D1D9] rounded-xl hover:bg-[#30363D]">Annulla</button>
          <button
            data-testid="save-company-btn"
            onClick={handleSave} disabled={saving}
            className="flex-1 px-4 py-3 bg-[#A371F7] text-white rounded-xl hover:bg-[#8B5CF6] flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
            {isEdit ? 'Salva' : 'Crea Azienda'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN APP ============

function App() {
  const [currentView, setCurrentView] = useState("loading");
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [prefillCustomer, setPrefillCustomer] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const perms = currentAccount?.permissions || {};
  const isAdmin = currentAccount?.role === "admin";

  // Check URL on mount — card pages are public, everything else needs PIN
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const card = params.get("card");
    
    if (card) {
      setCurrentView("public-card");
      return;
    }

    // Check session
    const auth = sessionStorage.getItem(SESSION_KEY);
    if (auth) {
      try {
        const acc = JSON.parse(auth);
        if (acc.role === "super_admin") {
          setIsSuperAdmin(true);
          setCurrentAccount(acc);
          setCurrentView("super-admin");
        } else if (acc.company_id) {
          setCurrentAccount(acc);
          setIsAuthenticated(true);
          routeToAdmin(params, acc);
        } else {
          sessionStorage.removeItem(SESSION_KEY);
          setCurrentView("pin");
        }
      } catch {
        sessionStorage.removeItem(SESSION_KEY);
        setCurrentView("pin");
      }
    } else {
      setCurrentView("pin");
    }
  }, []);

  const routeToAdmin = (params, acc) => {
    if (!params) params = new URLSearchParams(window.location.search);
    const orderId = params.get("order");
    const view = params.get("view");
    const p = acc?.permissions || {};

    if (orderId === "new" && p.orders) {
      setCurrentView("new");
    } else if (orderId && p.orders) {
      setSelectedOrderId(orderId);
      setCurrentView("detail");
    } else if (view === "orders" && p.orders) {
      setCurrentView("list");
    } else if (view === "customers" && p.customers) {
      setCurrentView("customers");
    } else if (view === "inventory" && p.inventory) {
      setCurrentView("inventory");
    } else if (view === "scanner" && p.scanner) {
      setCurrentView("scanner");
    } else if (view === "settings" && (acc?.role === "admin" || p.settings)) {
      setCurrentView("settings");
    } else {
      if (p.dashboard) setCurrentView("dashboard");
      else if (p.orders) setCurrentView("list");
      else if (p.scanner) setCurrentView("scanner");
      else setCurrentView("dashboard");
    }
  };

  const handleUnlock = (account) => {
    setCurrentAccount(account);
    setIsAuthenticated(true);
    setIsSuperAdmin(false);
    routeToAdmin(null, account);
  };

  const handleSuperAdmin = (account) => {
    setCurrentAccount(account);
    setIsSuperAdmin(true);
    setIsAuthenticated(false);
    setCurrentView("super-admin");
  };

  const handleNavigate = (view, orderId = null, customerData = null) => {
    if (view === "dashboard") {
      setCurrentView("dashboard");
      setSelectedOrderId(null);
      setPrefillCustomer(null);
      window.history.pushState(null, '', '/');
    } else if (view === "list") {
      setCurrentView("list");
      setSelectedOrderId(null);
      setPrefillCustomer(null);
      window.history.pushState(null, '', '?view=orders');
    } else if (view === "customers") {
      setCurrentView("customers");
      setSelectedOrderId(null);
      setPrefillCustomer(null);
      window.history.pushState(null, '', '?view=customers');
    } else if (view === "inventory") {
      setCurrentView("inventory");
      setSelectedOrderId(null);
      setPrefillCustomer(null);
      window.history.pushState(null, '', '?view=inventory');
    } else if (view === "scanner") {
      setCurrentView("scanner");
      setSelectedOrderId(null);
      setPrefillCustomer(null);
      window.history.pushState(null, '', '?view=scanner');
    } else if (view === "settings") {
      setCurrentView("settings");
      setSelectedOrderId(null);
      setPrefillCustomer(null);
      window.history.pushState(null, '', '?view=settings');
    } else if (view === "detail" && orderId) {
      setSelectedOrderId(orderId);
      setCurrentView("detail");
      setPrefillCustomer(null);
      window.history.pushState(null, '', `?order=${orderId}`);
    } else if (view === "new-order-customer" && customerData) {
      setPrefillCustomer(customerData);
      setSelectedOrderId(null);
      setCurrentView("new");
      window.history.pushState(null, '', '?order=new');
    } else if (view === "customer-from-order" && customerData) {
      setCurrentView("customer-from-order");
      setPrefillCustomer(customerData);
      setSelectedOrderId(null);
      window.history.pushState(null, '', '?view=customers');
    }
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrderId(orderId);
    setCurrentView("detail");
    window.history.pushState(null, '', `?order=${orderId}`);
  };

  const handleNewOrder = () => {
    setSelectedOrderId(null);
    setPrefillCustomer(null);
    setCurrentView("new");
    window.history.pushState(null, '', '?order=new');
  };

  const handleBack = () => {
    setCurrentView("list");
    setSelectedOrderId(null);
    setPrefillCustomer(null);
    window.history.pushState(null, '', '?view=orders');
  };

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setIsAuthenticated(false);
    setIsSuperAdmin(false);
    setCurrentAccount(null);
    setCurrentView("pin");
    window.history.pushState(null, '', '/');
  };

  return (
    <>
      {/* Public card — always accessible */}
      {currentView === "public-card" && (
        <PublicCardPage />
      )}

      {/* PIN lock screen */}
      {currentView === "pin" && (
        <PinLockScreen onUnlock={handleUnlock} onSuperAdmin={handleSuperAdmin} />
      )}

      {/* Super Admin Dashboard */}
      {currentView === "super-admin" && isSuperAdmin && (
        <SuperAdminDashboard onLogout={handleLogout} />
      )}

      {/* Loading */}
      {currentView === "loading" && (
        <div className="min-h-screen bg-[#0D1117] flex items-center justify-center">
          <Loader2 size={48} className="text-[#58A6FF] animate-spin" />
        </div>
      )}

      {/* Admin area — only when authenticated, respecting permissions */}
      {isAuthenticated && (
        <>
          {currentView === "dashboard" && perms.dashboard && (
            <Dashboard onNavigate={handleNavigate} isAdmin={isAdmin} perms={perms} accountName={currentAccount?.name} onLogout={handleLogout} />
          )}
          {currentView === "list" && perms.orders && (
            <OrdersList 
              onSelectOrder={handleSelectOrder}
              onNewOrder={handleNewOrder}
              onNavigate={handleNavigate}
              onLogout={handleLogout}
            />
          )}
          {(currentView === "customers" || currentView === "customer-from-order") && perms.customers && (
            <CustomersList
              onNavigate={handleNavigate}
              onSelectOrder={handleSelectOrder}
              autoSelectCustomer={currentView === "customer-from-order" ? prefillCustomer : null}
              onLogout={handleLogout}
            />
          )}
          {currentView === "inventory" && perms.inventory && (
            <InventoryPage onNavigate={handleNavigate} onLogout={handleLogout} />
          )}
          {currentView === "scanner" && perms.scanner && (
            <QrScannerPage onNavigate={handleNavigate} onLogout={handleLogout} />
          )}
          {currentView === "settings" && isAdmin && (
            <SettingsPage onNavigate={handleNavigate} currentAccount={currentAccount} />
          )}
          {currentView === "detail" && perms.orders && (
            <OrderDetail 
              orderId={selectedOrderId}
              onBack={handleBack}
              onNavigate={handleNavigate}
            />
          )}
          {currentView === "new" && perms.orders && (
            <OrderDetail 
              orderId={null}
              onBack={handleBack}
              isNew={true}
              prefillCustomer={prefillCustomer}
              onNavigate={handleNavigate}
            />
          )}
        </>
      )}
      <Toaster position="top-center" theme="dark" />
    </>
  );
}

export default App;
